import { HTTPStatusCode, SortOrder, BroadcastContentType, getEntityType, APIEntityType, AssetType, SearchContext, Operator, BooleanOperator, DataSourceType, extractMimeType, isSupportedSvgMimeType, ImageContentType, isSupportedImageMimeType } from "@ardoq/api-types";
import { isArdoqError, toArdoqError, getArdoqErrorMessage, resultsToPayloadOrError, isTruthy, ardoqId } from "@ardoq/common-helpers";
import { logError, logWarn } from "@ardoq/logging";
import { omit, pick, kebabCase } from "lodash";
import { pipe, catchError, of, tap, filter } from "rxjs";
import { isCloudProvider, isUnifiedIntegrationProvider } from "@ardoq/api-types/integrations";
class FetchError extends Error {
  constructor(message, context) {
    super(message);
    this.context = context;
    this.name = "FetchError";
  }
  getStatus() {
    return this.context.response.status;
  }
  getTraceId() {
    var _a;
    return ((_a = this.context.json) == null ? void 0 : _a.traceId) ?? "";
  }
  getMessage() {
    var _a;
    return ((_a = this.context.json) == null ? void 0 : _a.message) ?? this.message;
  }
  getData() {
    var _a;
    return (_a = this.context.json) == null ? void 0 : _a.data;
  }
}
const isFetchError = (error) => error instanceof FetchError;
const getErrorStatusCode = (error) => {
  if (isFetchError(error)) {
    return error.getStatus();
  }
  if (isArdoqError(error)) {
    return getErrorStatusCode(error.error);
  }
  return 0;
};
const UNAUTHORIZED_ERROR = "ajax-unauthorized-error";
const FALLBACK_ERROR_MESSAGE = `Something went wrong, and we're unable to create a new connection. Please try connecting again. Contact support if the problem continues.`;
const ARDOQ_TRACE_ID_HEADER = "x-ardoq-trace-id";
const defaultValueIfError = (response, defaultValue) => {
  return isArdoqError(response) ? defaultValue : response;
};
const normalize = (urlString) => {
  const url = new URL(urlString, window.origin);
  const parts = url.pathname.split("/");
  return parts.map((part) => part.replace(/[a-f|\d]{24}/g, "$id")).join("/");
};
var Method = /* @__PURE__ */ ((Method2) => {
  Method2["GET"] = "GET";
  Method2["POST"] = "POST";
  Method2["PUT"] = "PUT";
  Method2["DELETE"] = "DELETE";
  Method2["PATCH"] = "PATCH";
  return Method2;
})(Method || {});
const headers = {
  "Content-Type": "application/json"
};
const toBody = (body) => body ? { body: JSON.stringify(body) } : {};
const toRequestOptions = (method, body, signal) => ({
  method,
  headers,
  signal,
  ...toBody(body)
});
const toApiResponse = async (response) => {
  if (!response.ok) {
    return toResponseError(response);
  }
  return response.json();
};
const toBlobResponse = async (response) => {
  if (!response.ok) {
    return toResponseError(response);
  }
  return response.blob();
};
const getResponseErrorMessage = async (response) => {
  try {
    const json = await response.json();
    return json.message ?? json.errors ?? json.error;
  } catch (error) {
    return null;
  }
};
const getErrorInformation = async (response) => {
  const responseErrorMessage = await getResponseErrorMessage(response);
  const developerMessage = `${normalize(response.url)} ${response.status} - ${responseErrorMessage ?? response.statusText}`;
  const userMessage = responseErrorMessage ?? FALLBACK_ERROR_MESSAGE;
  const traceId = response.headers.get(ARDOQ_TRACE_ID_HEADER) ?? void 0;
  return {
    developerMessage,
    userMessage,
    traceId
  };
};
const toResponseError = async (response) => {
  if (response.ok) {
    throw new Error(
      `Received a successful response: ${response.status}, did you forget to check response.ok?`
    );
  }
  maybeEmitUnauthorizedEvent(response.status);
  const { developerMessage, userMessage, traceId } = await getErrorInformation(response);
  return toArdoqError({
    error: new FetchError(developerMessage, { response }),
    userMessage,
    traceId
  });
};
const toReturnWithResponse = async (response, data) => {
  if (!response.ok) {
    return toResponseError(response);
  }
  return data;
};
const fetchIntegrationJson = (request2) => window.fetch(request2).then(async (response) => {
  if (!response.ok) {
    const { developerMessage, userMessage, traceId } = await getErrorInformation(response);
    return toArdoqError({
      error: new FetchError(developerMessage, { response }),
      userMessage,
      traceId
    });
  }
  return response.json();
}).catch((error) => handleNetworkError(request2, error));
const fetchJson = (request2) => window.fetch(request2).then((response) => toApiResponse(response)).catch((error) => handleNetworkError(request2, error));
const fetchVoid = (request2) => fetchAndRespondWith(request2, void 0);
const fetchAndRespondWith = (request2, data) => window.fetch(request2).then((response) => toReturnWithResponse(response, data)).catch((error) => handleNetworkError(request2, error));
const fetchVoidAndRespondWithId = (request2, id) => fetchAndRespondWith(request2, id);
const fetchBlob = (request2) => window.fetch(request2).then((response) => toBlobResponse(response)).catch((error) => handleNetworkError(request2, error));
const handleNetworkError = (request2, error) => {
  var _a;
  const isAborted = Boolean((_a = request2.signal) == null ? void 0 : _a.aborted);
  if (isArdoqError(error)) {
    return error;
  }
  return toArdoqError({
    error: new Error(`Failed to fetch ${normalize(request2.url)}`, {
      cause: error
    }),
    isAborted
  });
};
const fetchEntity = (pathFragment2) => (id) => {
  const request2 = new Request(`/api/${pathFragment2}/${id}`, { headers });
  return fetchJson(request2);
};
const fetchEntities = (pathFragment2) => () => {
  const request2 = new Request(`/api/${pathFragment2}`, { headers });
  return fetchJson(request2);
};
const deleteEntity = (pathFragment2) => async (id) => {
  const request2 = new Request(
    `/api/${pathFragment2}/${id}`,
    toRequestOptions("DELETE", {})
  );
  const response = await fetchVoid(request2);
  if (isArdoqError(response)) {
    return response;
  }
  return id;
};
const createEntity = (pathFragment2) => (entity) => {
  const request2 = new Request(
    `/api/${pathFragment2}`,
    toRequestOptions("POST", entity)
  );
  return fetchJson(request2);
};
const updateEntity = (pathFragment2) => (entity) => {
  const request2 = new Request(
    `/api/${pathFragment2}/${entity._id}`,
    toRequestOptions("PUT", entity)
  );
  return fetchJson(request2);
};
const renameEntity = (pathFragment2) => async ({ name, _id, _version }) => {
  const request2 = new Request(
    `/api/${pathFragment2}/${_id}/rename`,
    toRequestOptions("PATCH", { name, _version })
  );
  return fetchJson(request2);
};
const crudApi = (pathFragment2) => ({
  create: createEntity(pathFragment2),
  update: updateEntity(pathFragment2),
  delete: deleteEntity(pathFragment2),
  fetch: fetchEntity(pathFragment2),
  fetchAll: fetchEntities(pathFragment2)
});
const shouldLog = (status) => !(status === HTTPStatusCode.UNAUTHORIZED || status === HTTPStatusCode.FORBIDDEN);
const logErrorIfNeeded = (response) => {
  if (isArdoqError(response)) {
    const statusCode = getErrorStatusCode(response);
    if (shouldLog(statusCode)) {
      logError(response);
    }
    return null;
  }
  return response;
};
const logErrorIfNeededAndReturnMessage = (error, defaultMessage) => {
  const message = getArdoqErrorMessage(error, defaultMessage);
  const statusCode = getErrorStatusCode(error);
  if (shouldLog(statusCode)) {
    logError(error, message);
  }
  return message;
};
const maybeEmitUnauthorizedEvent = (status) => {
  if (status === HTTPStatusCode.UNAUTHORIZED) {
    document.dispatchEvent(new CustomEvent(UNAUTHORIZED_ERROR));
  }
};
const isHttpError = (statusCode, error) => getErrorStatusCode(error) === statusCode;
const isServiceUnavailable = (error) => isHttpError(HTTPStatusCode.SERVICE_UNAVAILABLE, error);
const isBadRequest = (error) => isHttpError(HTTPStatusCode.BAD_REQUEST, error);
const isUnauthorized = (error) => isHttpError(HTTPStatusCode.UNAUTHORIZED, error);
const isConflict = (error) => isHttpError(HTTPStatusCode.CONFLICT, error);
const isUnsupportedMediaType = (error) => isHttpError(HTTPStatusCode.UNSUPPORTED_MEDIA_TYPE, error);
const isForbidden = (error) => isHttpError(HTTPStatusCode.FORBIDDEN, error);
const isNotFound = (error) => isHttpError(HTTPStatusCode.NOT_FOUND, error);
const isCreated = (error) => isHttpError(HTTPStatusCode.CREATED, error);
const isUnprocessable = (error) => isHttpError(HTTPStatusCode.UNPROCESSABLE_ENTITY, error);
const api = {
  isConflict,
  isBadRequest,
  isUnsupportedMediaType,
  isUnauthorized,
  isForbidden,
  isNotFound,
  isCreated,
  isUnprocessable,
  isServiceUnavailable,
  defaultValueIfError,
  logErrorIfNeeded,
  logErrorIfNeededAndReturnMessage,
  shouldLog,
  toApiResponse
};
const toQueryString = ({
  from = 0,
  size = 100,
  sortOrder = SortOrder.DEFAULT,
  sortBy = "",
  branchId = null
}) => {
  const params = new URLSearchParams({
    from: from.toString(),
    size: size.toString(),
    order: sortOrder === SortOrder.DEFAULT ? "" : sortOrder,
    sort: sortBy
  });
  if (branchId) {
    params.set("branchId", branchId);
  }
  return params.toString();
};
const defaultParams = { from: 0, size: 100 };
const search$3 = (query, searchParams = defaultParams) => {
  const queryString = toQueryString(searchParams);
  const request2 = new Request(
    `/api/advanced-search?${queryString}`,
    toRequestOptions(Method.POST, query)
  );
  return fetchJson(request2);
};
const fieldSearch = (query, { branchId } = {}) => {
  const queryString = branchId ? `?branchId=${branchId}` : "";
  const request2 = new Request(
    `/api/advanced-search-fields${queryString}`,
    toRequestOptions(Method.POST, query)
  );
  return fetchJson(request2);
};
const exportSearch = async (query, searchParams = defaultParams) => {
  const queryString = toQueryString(searchParams);
  const request2 = new Request(
    `/api/advanced-search-export?${queryString}`,
    toRequestOptions(Method.POST, query)
  );
  return fetchBlob(request2);
};
const scopeData = (payload, abortSignal) => {
  const request2 = new Request(
    "/api/advanced-search-scopedata",
    toRequestOptions(Method.POST, payload, abortSignal)
  );
  return fetchJson(request2);
};
const advancedSearchApi = {
  search: search$3,
  fieldSearch,
  exportSearch,
  scopeData
};
const allowlistApi = crudApi(
  "organization/whitelist/configure"
);
const getAISuggestions = async (surveyId, questionId, componentId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/question/${questionId}/component-suggestion/${componentId}`,
    { headers }
  );
  return fetchJson(request2);
};
const aiSuggestionApi = {
  getAISuggestions
};
const workspaceFolderApi = {
  moveAssets: createEntity("workspacefolder/asset/move"),
  create: createEntity("workspacefolder"),
  rename: renameEntity("workspacefolder"),
  fetchAll: fetchEntities("workspacefolder")
};
const deleteAssets = (assets) => {
  const request2 = new Request(
    `/api/asset/delete`,
    toRequestOptions(Method.POST, { assets })
  );
  return fetchJson(request2);
};
const createInFolder$2 = async (asset, folderId, assetType) => {
  const assetResponse = await createEntity(assetType)(asset);
  if (isArdoqError(assetResponse)) {
    return assetResponse;
  }
  const moveAssetsResponse = await workspaceFolderApi.moveAssets({
    targetFolderId: folderId,
    content: [{ _id: assetResponse._id, type: assetType }]
  });
  if (isArdoqError(moveAssetsResponse)) {
    return moveAssetsResponse;
  }
  return assetResponse;
};
const assetUsage = async ({ type, id }) => {
  const request2 = new Request(
    `/api/asset/${type}/${id}/usage`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const assetApi = {
  deleteAssets,
  createInFolder: createInFolder$2,
  assetUsage
};
const getOrgUrl$1 = (orgId) => `/api/folder/org/${orgId}/folder`;
const getItemFromOrgUrl$1 = (orgId, itemId) => `${getOrgUrl$1(orgId)}/${itemId}`;
const getWorkspaceUrl$1 = (workspaceId) => `/api/folder/workspace/${workspaceId}/folder`;
const getItemFromWorkspaceUrl$1 = (orgId, itemId) => `${getWorkspaceUrl$1(orgId)}/${itemId}`;
const fetchWorkspaceFolders = async (workspaceId) => {
  const request2 = new Request(`/api/folder/workspace/${workspaceId}/folder`, {
    headers
  });
  return fetchJson(request2);
};
const fetchOrgFolders = async (orgId) => {
  const request2 = new Request(`/api/folder/org/${orgId}/folder`, {
    headers
  });
  return fetchJson(request2);
};
const rename$1 = ({
  type,
  typeId,
  renameRequest
}) => {
  const request2 = new Request(
    `/api/folder/${type}/${typeId}/folder/${renameRequest._id}`,
    toRequestOptions(Method.PATCH, renameRequest)
  );
  return fetchJson(request2);
};
const moveMultipleToFolder$1 = async ({
  type,
  typeId,
  foldersWithNewParent
}) => {
  const requests = foldersWithNewParent.map((folder) => {
    return moveFolderToFolder(type, typeId, folder);
  });
  const responses = await Promise.all(requests);
  return resultsToPayloadOrError(responses);
};
const moveFolderToFolder = (type, typeId, folderWithNewParent) => {
  const request2 = new Request(
    `/api/folder/${type}/${typeId}/folder/${folderWithNewParent._id}`,
    toRequestOptions(Method.PUT, folderWithNewParent)
  );
  return fetchJson(request2);
};
const create$7 = async ({
  name,
  type,
  typeId
}) => {
  const request2 = new Request(
    `/api/folder/${type}/${typeId}/folder`,
    toRequestOptions(Method.POST, {
      name,
      description: "",
      // Adding context here is a hack that needs to be fixed in the backend. There is currently
      // no implementation to infer the context from the url.
      context: type === "org" ? "DocumentArchiveContext/ORG" : "DocumentArchiveContext/WS"
    })
  );
  return fetchJson(request2);
};
const folderApi = {
  fetchWorkspaceFolders,
  fetchOrgFolders,
  getOrgUrl: getOrgUrl$1,
  getItemFromOrgUrl: getItemFromOrgUrl$1,
  getWorkspaceUrl: getWorkspaceUrl$1,
  getItemFromWorkspaceUrl: getItemFromWorkspaceUrl$1,
  rename: rename$1,
  delete: deleteEntity("folder"),
  moveMultipleToFolder: moveMultipleToFolder$1,
  create: create$7
};
const getOrgUrl = (orgId) => `/api/attachment/org/${orgId}`;
const getItemFromOrgUrl = (orgId, itemId) => `${getOrgUrl(orgId)}/${itemId}`;
const getWorkspaceUrl = (workspaceId) => `/api/attachment/workspace/${workspaceId}`;
const getItemFromWorkspaceUrl = (orgId, itemId) => `${getWorkspaceUrl(orgId)}/${itemId}`;
const transformToGlobal = ({
  workspaceId,
  attachmentAndFolderIds
}) => {
  const request2 = new Request(
    `/api/attachment/workspace/${workspaceId}/make-global`,
    toRequestOptions(Method.PUT, attachmentAndFolderIds)
  );
  return fetchJson(request2);
};
const getWorkspaceAttachments = (id) => {
  const request2 = new Request(
    `/api/attachment/workspace/${id}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const getOrgAttachments = (id) => {
  const request2 = new Request(`/api/attachment/org/${id}`, { headers });
  return fetchJson(request2);
};
const uploadFiles = async ({
  files,
  type,
  typeId,
  folderId
}) => {
  let url = `/api/attachment/${type}/${typeId}/upload`;
  if (folderId) {
    url = url.concat(`?folder=${folderId}`);
  }
  const requests = files.map((file) => {
    const formData = new FormData();
    formData.append("attachment", file);
    return fetchJson(
      new Request(url, {
        method: Method.POST,
        body: formData
      })
    );
  });
  const results = await Promise.all(requests);
  return resultsToPayloadOrError(results);
};
const rename = ({
  type,
  typeId,
  renameRequest
}) => {
  const request2 = new Request(
    `/api/attachment/${type}/${typeId}/${renameRequest._id}`,
    //
    toRequestOptions(Method.PATCH, renameRequest)
  );
  return fetchJson(request2);
};
const deleteMultiple = async (ids) => {
  const requests = ids.map((id) => attachmentApi.delete(id));
  const responses = await Promise.all(requests);
  return resultsToPayloadOrError(responses);
};
const moveMultipleToFolder = async ({
  type,
  typeId,
  attachmentsWithNewFolder
}) => {
  const requests = attachmentsWithNewFolder.map(
    (attachment) => moveToFolder(type, typeId, attachment)
  );
  const responses = await Promise.all(requests);
  return resultsToPayloadOrError(responses);
};
const moveToFolder = (type, typeId, attachmentWithNewFolder) => {
  const request2 = new Request(
    `/api/attachment/${type}/${typeId}/${attachmentWithNewFolder._id}`,
    toRequestOptions(Method.PUT, attachmentWithNewFolder)
  );
  return fetchJson(request2);
};
const fetchAttachments = async (workspaceId, organizationId) => {
  const responses = await Promise.all([
    getWorkspaceAttachments(workspaceId),
    getOrgAttachments(organizationId)
  ]);
  const results = resultsToPayloadOrError(
    responses,
    "Failed to fetch attachments"
  );
  if (isArdoqError(results)) {
    return results;
  }
  return results.flat();
};
const fetchFolders = async (workspaceId, organizationId) => {
  const responses = await Promise.all([
    folderApi.fetchWorkspaceFolders(workspaceId),
    folderApi.fetchOrgFolders(organizationId)
  ]);
  const results = resultsToPayloadOrError(responses, "Failed to fetch folders");
  if (isArdoqError(results)) {
    return results;
  }
  return results.flat();
};
const fetchAttachmentsAndFolders = async ({
  workspaceId,
  orgId
}) => {
  const workspaceAttachmentsRequest = getWorkspaceAttachments(workspaceId);
  const orgAttachmentsRequest = getOrgAttachments(orgId);
  const workspaceFoldersRequest = folderApi.fetchWorkspaceFolders(workspaceId);
  const orgFoldersRequest = folderApi.fetchOrgFolders(orgId);
  const responses = await Promise.all([
    workspaceAttachmentsRequest,
    orgAttachmentsRequest,
    workspaceFoldersRequest,
    orgFoldersRequest
  ]);
  const errors = responses.filter(isArdoqError);
  if (errors.length) {
    return toArdoqError({
      error: new AggregateError(
        errors,
        getArdoqErrorMessage(errors[0]) || "Failed to fetch attachments and folders"
      )
    });
  }
  return {
    workspaceAttachments: responses[0],
    orgAttachments: responses[1],
    workspaceFolders: responses[2],
    orgFolders: responses[3]
  };
};
const attachmentApi = {
  transformToGlobal,
  getWorkspaceAttachments,
  getOrgAttachments,
  getOrgUrl,
  getItemFromOrgUrl,
  getWorkspaceUrl,
  getItemFromWorkspaceUrl,
  uploadFiles,
  rename,
  delete: deleteEntity("attachment"),
  deleteMultiple,
  moveMultipleToFolder,
  fetchAttachments,
  fetchFolders,
  fetchAttachmentsAndFolders
};
const fetchSuggestions = (suggestionType, query, body) => {
  const request2 = new Request(
    `/api/audit-log/change-log/suggestion/${suggestionType}?q=${encodeURIComponent(
      query
    )}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const fetchChangeLog = (body) => {
  const request2 = new Request(
    "/api/audit-log/change-log",
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const restoreDeletedComponent = (componentId) => {
  const request2 = new Request(
    "/api/restore/v2/component",
    toRequestOptions(Method.POST, {
      component: componentId
    })
  );
  return fetchVoidAndRespondWithId(request2, componentId);
};
const restoreDeletedReference = (referenceId) => {
  const request2 = new Request(
    "/api/restore/v2/reference",
    toRequestOptions(Method.POST, {
      reference: referenceId
    })
  );
  return fetchVoidAndRespondWithId(request2, referenceId);
};
const auditLogApi = {
  fetchSuggestions,
  fetchChangeLog,
  restoreDeletedComponent,
  restoreDeletedReference
};
const fetchLoginConfig = () => {
  const request2 = new Request("/auth/login-config", { headers });
  return fetchJson(request2);
};
const fetchChooseOrgOptions = () => {
  const request2 = new Request("/auth/choose-org-options", { headers });
  return fetchJson(request2);
};
const authApi = {
  fetchLoginConfig,
  fetchChooseOrgOptions
};
const BASE_API_URL$5 = `/api/integrations/cloud/aws`;
const CONNECTIONS_API_URL$1 = "/api/integrations/account/aws";
async function fetchVpcs({
  connectionId,
  async,
  requestId,
  regions = []
}) {
  const VPCS_URL = `${BASE_API_URL$5}/resources/vpcs?async=${async}&requestId=${requestId}`;
  const request2 = new Request(
    VPCS_URL,
    toRequestOptions(Method.POST, {
      authId: connectionId,
      authMethod: "saved",
      providerParams: {
        ...regions.length > 0 && { regions }
      }
    })
  );
  return fetchIntegrationJson(request2);
}
async function fetchSettings() {
  const request2 = new Request(`${BASE_API_URL$5}/settings`, { headers });
  return fetchJson(request2);
}
async function fetchConnectionsList$4() {
  const request2 = new Request(CONNECTIONS_API_URL$1, { headers });
  return fetchJson(request2);
}
async function pingConnection$2(payload) {
  const request2 = new Request(
    `${BASE_API_URL$5}/validate-credentials`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchIntegrationJson(request2);
}
async function createConnection$3(payload) {
  const request2 = new Request(
    CONNECTIONS_API_URL$1,
    toRequestOptions(Method.POST, payload)
  );
  return fetchIntegrationJson(request2);
}
async function deleteConnection$3(connection) {
  const request2 = new Request(
    `${CONNECTIONS_API_URL$1}/${connection._id}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
}
async function updateConnection$3(id, payload) {
  const request2 = new Request(
    `${CONNECTIONS_API_URL$1}/${id}`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchVoid(request2);
}
const awsIntegrationApi = {
  fetchVpcs,
  fetchSettings,
  fetchConnectionsList: fetchConnectionsList$4,
  pingConnection: pingConnection$2,
  createConnection: createConnection$3,
  deleteConnection: deleteConnection$3,
  updateConnection: updateConnection$3
};
const BASE_API_URL$4 = `/api/integrations/cloud/azure`;
const CONNECTIONS_API_URL = "/api/integrations/account/azure";
async function fetchResourcesSummary$1({
  connectionId,
  async,
  requestId,
  subscription,
  resourceGroup
}) {
  const request2 = new Request(
    `${BASE_API_URL$4}/resources-summary?&async=${async}&requestId=${requestId}`,
    toRequestOptions(Method.POST, {
      authId: connectionId,
      authMethod: "saved",
      subscription,
      resourceGroup
    })
  );
  return fetchIntegrationJson(request2);
}
async function fetchConnectionsList$3() {
  const request2 = new Request(CONNECTIONS_API_URL, { headers });
  return fetchJson(request2);
}
async function pingConnection$1(payload) {
  const request2 = new Request(
    `${BASE_API_URL$4}/validate-credentials`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchIntegrationJson(request2);
}
async function createConnection$2(payload) {
  const request2 = new Request(
    CONNECTIONS_API_URL,
    toRequestOptions(Method.POST, payload)
  );
  return fetchIntegrationJson(request2);
}
async function deleteConnection$2(connection) {
  const request2 = new Request(
    `${CONNECTIONS_API_URL}/${connection._id}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
}
async function updateConnection$2(id, payload) {
  const request2 = new Request(
    `${CONNECTIONS_API_URL}/${id}`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchVoid(request2);
}
const azureIntegrationApi = {
  fetchResourcesSummary: fetchResourcesSummary$1,
  fetchConnectionsList: fetchConnectionsList$3,
  pingConnection: pingConnection$1,
  createConnection: createConnection$2,
  deleteConnection: deleteConnection$2,
  updateConnection: updateConnection$2
};
const execute = (batch) => {
  const request2 = new Request(
    `/api/batch/v2`,
    toRequestOptions(Method.POST, batch)
  );
  return fetchJson(request2);
};
const batchApi = {
  execute
};
const startChat = async () => {
  const request2 = new Request(
    "/api/best-practice-assistant/start-chat",
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
};
const sendMessage = ({
  clientRequestId,
  ...body
}) => {
  const request2 = new Request(
    `/api/best-practice-assistant/send-message/async?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoid(request2);
};
const submitFeedback = async (feedback) => {
  const request2 = new Request(
    "/api/best-practice-assistant/feedback",
    toRequestOptions(Method.POST, feedback)
  );
  return fetchVoid(request2);
};
const bestPracticeAssistantApi = {
  startChat,
  sendMessage,
  submitFeedback
};
const messageBroadcastContentAttributes = ["contentType", "workspaceId", "componentType", "filterConditions"];
const surveyBroadcastContentAttributes = [
  "contentType",
  "contentId",
  "filterConditions"
];
const reportBroadcastContentAttributes = [
  "contentType",
  "contentId",
  "columnName",
  "aggregate",
  "threshold",
  "predicate"
];
const includeOnlyContentTypeSpecificAttributes = (broadcast) => {
  switch (broadcast.content.contentType) {
    case BroadcastContentType.MESSAGE:
      return {
        ...broadcast,
        content: pick(broadcast.content, messageBroadcastContentAttributes)
      };
    case BroadcastContentType.SURVEY:
      return {
        ...broadcast,
        content: pick(broadcast.content, surveyBroadcastContentAttributes)
      };
    case BroadcastContentType.REPORT:
      return {
        ...broadcast,
        content: pick(broadcast.content, reportBroadcastContentAttributes)
      };
    default:
      return broadcast;
  }
};
const previewMail = async (broadcastId, email) => {
  const request2 = new Request(
    `/api/broadcast/${broadcastId}/preview/mail`,
    toRequestOptions(Method.POST, { email })
  );
  return fetchJson(request2);
};
const getInstances = async (broadcastId) => {
  const request2 = new Request(`/api/broadcast/${broadcastId}/instances`, {
    headers
  });
  return fetchJson(request2);
};
const fetchBroadcastInstanceProgress = (broadcastInstanceId) => {
  const request2 = new Request(
    `/api/broadcast/instances/by-id/${broadcastInstanceId}/progress`,
    { headers }
  );
  return fetchJson(request2);
};
const fetchByContentId = async (contentId) => {
  const request2 = new Request(
    `/api/broadcast/content/by-id/${contentId}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const create$6 = async (body) => {
  const request2 = new Request(
    "/api/broadcast",
    toRequestOptions(
      Method.POST,
      omit(includeOnlyContentTypeSpecificAttributes(body), [
        "_id",
        "_version",
        "status",
        "nextRuntime"
      ])
    )
  );
  return fetchJson(request2);
};
const setStatus = async (broadcastId, status) => {
  const request2 = new Request(
    `/api/broadcast/${broadcastId}/status`,
    toRequestOptions(Method.PUT, { status })
  );
  return fetchJson(request2);
};
const trigger = async (broadcastId) => {
  const request2 = new Request(
    `/api/broadcast/${broadcastId}/trigger`,
    toRequestOptions(Method.POST, null)
  );
  return fetchVoid(request2);
};
const getPredefinedQueryOptions = async (content) => {
  const request2 = new Request(
    "/api/broadcast/audiences/predefinedoptions",
    toRequestOptions(Method.POST, content)
  );
  return fetchJson(request2);
};
const previewComponents = async (content) => {
  const request2 = new Request(
    "/api/broadcast/components",
    toRequestOptions(Method.POST, content)
  );
  return fetchJson(request2);
};
const getAudience = async (content, audiences) => {
  const request2 = new Request(
    "/api/broadcast/audiences-preview",
    toRequestOptions(Method.POST, { content, audiences })
  );
  return fetchJson(request2);
};
const copy$a = async (broadcast, newName) => {
  return broadcastApi.create({ ...broadcast, name: newName });
};
const broadcastApi = {
  create: create$6,
  update: updateEntity("broadcast"),
  fetchBroadcastInstanceProgress,
  rename: renameEntity("broadcast"),
  fetchByContentId,
  fetchAll: fetchEntities("broadcast"),
  delete: deleteEntity("broadcast"),
  setStatus,
  trigger,
  previewMail,
  getInstances,
  getPredefinedQueryOptions,
  previewComponents,
  getAudience,
  copy: copy$a
};
const copy$9 = async (bundleId, body) => {
  const request2 = new Request(
    `/api/bundle/${bundleId}/copy`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const copyAsync = async (bundleId, body) => {
  const request2 = new Request(
    `/api/bundle/${bundleId}/copy-async`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const validate = async (bundleId) => {
  const request2 = new Request(
    `/api/bundle/${bundleId}/validate`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson(request2);
};
const validateCopyConfig = async (copyConfig) => {
  const request2 = new Request(
    `/api/bundle/validate-copy-config`,
    toRequestOptions(Method.POST, copyConfig)
  );
  return fetchJson(request2);
};
const bundleApi = {
  ...crudApi("bundle"),
  copy: copy$9,
  copyAsync,
  validate,
  validateCopyConfig
};
const BASE_API_URL$3 = `/api/integrations/cloud`;
const fetchRegions = async (payload, provider) => {
  const request2 = new Request(
    `${BASE_API_URL$3}/${provider}/resources/regions`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchIntegrationJson(request2);
};
async function fetchResourcesSummary({
  connectionId,
  subscription,
  resourceGroup,
  provider
}) {
  const request2 = new Request(
    `${BASE_API_URL$3}/${provider}/resources-summary`,
    toRequestOptions(Method.POST, {
      authId: connectionId,
      authMethod: "saved",
      subscription,
      resourceGroup
    })
  );
  return fetchIntegrationJson(request2);
}
async function fetchResourceTypes(provider) {
  const request2 = new Request(`${BASE_API_URL$3}/${provider}/resources`);
  return fetchIntegrationJson(request2);
}
const fetchSelection$1 = async (selection, provider, async = false, requestId) => {
  const url = `/api/integrations/tabular/cloud/${provider}?async=${async}&requestId=${requestId}`;
  const request2 = new Request(url, toRequestOptions(Method.POST, selection));
  return fetchIntegrationJson(request2).then((result) => ({
    ...result,
    async
  }));
};
const cloudProviderApi = {
  fetchRegions,
  fetchResourcesSummary,
  fetchResourceTypes,
  fetchSelection: fetchSelection$1
};
const deleteConfiguration = async (id) => {
  const request2 = new Request(
    `/api/integrations/tabular/mapping/${id}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoid(request2);
};
const updateConfiguration = async (config) => {
  const request2 = new Request(
    `/api/integrations/tabular/mapping/${config._id}`,
    toRequestOptions(Method.PUT, config)
  );
  return fetchJson(request2);
};
async function createConfiguration(config) {
  const request2 = new Request(
    "/api/integrations/tabular/mapping",
    toRequestOptions(Method.POST, config)
  );
  return fetchJson(request2);
}
const fetchTransferConfigs = async () => {
  const request2 = new Request("/api/integrations/tabular/mapping", { headers });
  return fetchJson(request2);
};
const fetchPreviewRows = async ({
  sourceId,
  tableId,
  startRow,
  rowCount
}) => {
  const request2 = new Request(
    `/api/integrations/tabular/source/${sourceId}/table/${tableId}?startRow=${startRow}&numRows=${rowCount}`,
    { headers }
  );
  return fetchJson(request2);
};
const commonIntegrationApi = {
  deleteConfiguration,
  updateConfiguration,
  createConfiguration,
  fetchTransferConfigs,
  fetchPreviewRows
};
const deleteComponents$1 = (componentIds) => {
  const request2 = new Request(
    `/api/component`,
    toRequestOptions(Method.DELETE, { componentIds })
  );
  return fetchJson(request2);
};
const copyComponents = async (targetWorkspace, parent, componentIds) => {
  const request2 = new Request(
    "/api/component/copy",
    toRequestOptions(Method.POST, { targetWorkspace, parent, componentIds })
  );
  return fetchJson(request2);
};
const scopedata$1 = async (entityIds) => {
  const request2 = new Request(
    `/api/component/scopedata`,
    toRequestOptions(Method.POST, { "component-ids": entityIds })
  );
  return fetchJson(request2).then(
    (result) => {
      if (isArdoqError(result)) {
        return result;
      }
      return { permissions: [], ...result };
    }
  );
};
const subtreeInfo$1 = async (componentIds) => {
  const request2 = new Request(
    `/api/component/subtree-info`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson(request2);
};
const generateDescription$3 = async (body) => {
  const request2 = new Request(
    `/api/component/autocomplete/description`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const merge$1 = ({
  mergedEntity,
  entityIdsToDelete
}) => {
  const request2 = new Request(
    `/api/component/${mergedEntity._id}/merge`,
    toRequestOptions(Method.PUT, {
      "subsumed-component-ids": entityIdsToDelete,
      "merged-component-data": mergedEntity
    })
  );
  return fetchJson(request2);
};
const restore$1 = async (payload) => {
  const entityAttributes = await componentApi.fetch(payload.entityId);
  if (isArdoqError(entityAttributes)) {
    return entityAttributes;
  }
  return componentApi.updateComponent({
    ...entityAttributes,
    [payload.fieldName]: payload.restorableValue
  });
};
const componentApi = {
  deleteComponents: deleteComponents$1,
  updateComponent: updateEntity("component"),
  createComponent: createEntity("component"),
  copyComponents,
  fetch: fetchEntity("component"),
  scopedata: scopedata$1,
  subtreeInfo: subtreeInfo$1,
  generateDescription: generateDescription$3,
  merge: merge$1,
  restore: restore$1
};
const componentTypeApi = {
  fetchAll: fetchEntities("component-type")
};
const deleteDashboard = deleteEntity("dashboard");
const copy$8 = (body) => {
  const request2 = new Request(
    `/api/dashboard/copy`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const create$5 = (body) => {
  const request2 = new Request(
    `/api/dashboard`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const update$6 = (id, body) => {
  const request2 = new Request(
    `/api/dashboard/${id}`,
    toRequestOptions(Method.PUT, body)
  );
  return fetchJson(request2);
};
const widgetPreview = (widgetData) => {
  const request2 = new Request(
    `/api/dashboard/widget-preview`,
    toRequestOptions(Method.POST, widgetData)
  );
  return fetchJson(request2);
};
const fetch$2 = (id, branchId) => {
  const url = new URL(`/api/dashboard/${id}/data`, window.location.origin);
  if (branchId) {
    url.searchParams.set("branchId", branchId);
  }
  const request2 = new Request(url, { headers });
  return fetchJson(request2);
};
const dashboardApi = {
  fetch: fetch$2,
  fetchAll: fetchEntities("dashboard"),
  rename: renameEntity("dashboard"),
  delete: deleteDashboard,
  copy: copy$8,
  create: create$5,
  update: update$6,
  widgetPreview
};
const dashboardColorPaletteApi = {
  ...crudApi("dashboard-color-palette")
};
const datasourceApi = {
  fetchAll: fetchEntities("datasource/v2")
};
const apiUrl = (() => {
  if (process.env.NODE_ENV === "test") {
    return `http://ardoq-fake-test.com/api`;
  }
  return `/api`;
})();
const fetchAll$3 = () => {
  const request2 = new Request(`${apiUrl}/discover-link`);
  return fetchJson(request2);
};
const update$5 = (discoverLinks) => {
  const request2 = new Request(
    `${apiUrl}/discover-link/action/batch`,
    toRequestOptions(Method.POST, { discoverLinks })
  );
  return fetchJson(request2);
};
const deleteQuickAccessLink = (quickAccessLinkId) => {
  const request2 = new Request(
    `${apiUrl}/discover-link/${quickAccessLinkId}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
};
const discoverLinkApi = {
  fetchAll: fetchAll$3,
  update: update$5,
  delete: deleteQuickAccessLink
};
const fetchConfiguration = async (organizationId) => {
  const request2 = new Request(
    `/api/organization/${organizationId}/welcome-text`,
    { headers }
  );
  return fetchJson(request2);
};
const saveConfiguration = async (organizationId, welcomeText) => {
  const request2 = new Request(
    `/api/organization/${organizationId}/welcome-text`,
    toRequestOptions(Method.PUT, welcomeText)
  );
  return fetchJson(request2);
};
const discoverConfigurationApi = {
  fetchConfiguration,
  saveConfiguration
};
const runDynamicFilter = (id, queryParams) => {
  const request2 = new Request(
    `/api/dynamic-filter/${id}`,
    toRequestOptions(Method.POST, { queryParams })
  );
  return fetchJson(request2);
};
const dynamicFilterApi = {
  runDynamicFilter
};
const entityGroupApi = {
  ...crudApi("entity-group")
};
const getEndPoint = (orgId) => `/api/organization/${orgId}/features`;
const updateFeatures = (orgId, features) => {
  const request2 = new Request(
    getEndPoint(orgId),
    toRequestOptions(Method.PUT, { features })
  );
  return fetchVoid(request2);
};
const deleteFeatures = (orgId, features) => {
  const request2 = new Request(
    getEndPoint(orgId),
    toRequestOptions(Method.DELETE, { features })
  );
  return fetchVoid(request2);
};
const featuresApi = {
  updateFeatures,
  deleteFeatures
};
const usage$2 = (id) => {
  const request2 = new Request(`/api/field/${id}/usage`, { headers });
  return fetchJson(request2);
};
const localUsage = (id) => {
  const request2 = new Request(`/api/field/${id}/usage?scope=local`, {
    headers
  });
  return fetchJson(request2);
};
const globalFields = () => {
  const request2 = new Request("/api/field/global/all", {
    headers
  });
  return fetchJson(request2);
};
const recalculate = (fieldId, branchId) => {
  const request2 = new Request(
    `/api/field/${fieldId}/recalculate${branchId ? `?branchId=${branchId}` : ""}`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson(request2);
};
const deleteGlobally = (fieldId) => {
  const request2 = new Request(
    `/api/field/${fieldId}?global=true`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchAndRespondWith(request2, fieldId);
};
const fieldApi = {
  usage: usage$2,
  localUsage,
  globalFields,
  recalculate,
  deleteGlobally,
  ...crudApi("field")
};
const MAX_GREMLIN_RESULTS_RETURNED = 1e5;
const allResults = { size: MAX_GREMLIN_RESULTS_RETURNED };
const graphSearchExport = async ({
  query,
  queryParams
}) => {
  const request2 = new Request(
    "/api/graph-search-export",
    toRequestOptions(Method.POST, { query, queryParams })
  );
  return fetchBlob(request2);
};
const graphSearch = ({
  query,
  queryParams
}) => {
  const request2 = new Request(
    "/api/graph-search",
    toRequestOptions(Method.POST, { query, queryParams })
  );
  return fetchJson(request2);
};
const parameterizedGraphSearch = (parameterQueryId, options) => {
  const url = new URL(
    `/api/graph-search/${parameterQueryId}/run`,
    window.location.origin
  );
  if (options == null ? void 0 : options.size) {
    url.searchParams.append("size", options.size.toString());
  }
  if (options == null ? void 0 : options.componentProps) {
    url.searchParams.append("componentProps", options.componentProps);
  }
  const request2 = new Request(url, {
    headers
  });
  return fetchJson(request2);
};
const graphSearchApi = {
  graphSearchExport,
  allResults,
  graphSearch,
  parameterizedGraphSearch
};
const manageUsers = (groupId, userIds, operation, queryParams = {}) => {
  const searchParams = new URLSearchParams(queryParams).toString();
  const request2 = new Request(
    `/api/group/${groupId}/${operation}${searchParams ? `?${searchParams}` : ""}`,
    toRequestOptions(Method.PUT, { userIds })
  );
  return fetchAndRespondWith(request2, userIds);
};
const addUsers = ({
  groupId,
  userIds
}) => manageUsers(
  groupId,
  userIds,
  "add"
  /* ADD */
);
const removeUsers = ({
  groupId,
  userIds,
  force
}) => manageUsers(
  groupId,
  userIds,
  "remove",
  force ? { force } : void 0
);
const createGroup = ({
  name
}) => {
  const request2 = new Request(
    `/api/group/`,
    toRequestOptions(Method.POST, { name })
  );
  return fetchJson(request2);
};
const createGroupWithUsers = async ({
  name,
  users
}) => {
  const group = await createGroup({ name });
  if (isArdoqError(group)) {
    return group;
  }
  const addedUsers = await addUsers({
    groupId: group._id,
    userIds: users.map((user) => user._id)
  });
  if (isArdoqError(addedUsers)) {
    return addedUsers;
  }
  return group;
};
const groupApi = {
  ...crudApi("group"),
  create: createGroup,
  addUsers,
  removeUsers,
  createGroupWithUsers
};
const fetchSuggestion = async () => {
  const request2 = new Request("/api/inference/suggestion", { headers });
  return fetchJson(request2);
};
const inferenceApi = {
  delete: deleteEntity("inference"),
  fetchSuggestion
};
const historyApi = {
  fetchAll: fetchEntities("integrations/history")
};
async function uploadFile(body) {
  const request2 = new Request("/api/integrations/tabular/upload", {
    method: Method.POST,
    body
  });
  return fetch(request2).then(toApiResponse).catch((error) => handleNetworkError(request2, error));
}
const tabularApi = {
  uploadFile
};
const loadData = async (payload, integrationId, requestId, async = true) => {
  const request2 = new Request(
    `/api/integrations/unified/${integrationId}/fetch?requestId=${requestId}&async=${async}`,
    toRequestOptions(Method.POST, payload)
  );
  return fetch(request2).then(toApiResponse).then((formatted) => ({ ...formatted, async, integrationId })).catch((error) => handleNetworkError(request2, error));
};
const unifiedApi = {
  loadData
};
const integrationsJobApi = {
  fetchAll: fetchEntities("integrations/job")
};
const BASE_URL$2 = "/api/integrations";
const authorize = (provider) => {
  const request2 = new Request(
    `${BASE_URL$2}/${provider}/authorize`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const callback = ({
  provider,
  code,
  state
}) => {
  const request2 = new Request(
    `${BASE_URL$2}/${provider}/callback`,
    toRequestOptions(Method.POST, { code, state })
  );
  return fetchVoid(request2);
};
const integrationsOAuthApi = {
  authorize,
  callback
};
const BASE_URL$1 = "/api/integrations/lucidchart";
const embedToken = (externalDocumentId) => {
  const request2 = new Request(
    `${BASE_URL$1}/embed-token`,
    toRequestOptions(Method.POST, { externalDocumentId })
  );
  return fetchJson(request2);
};
const pickerToken = () => {
  const request2 = new Request(
    `${BASE_URL$1}/picker-token`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const exportVisualization = (data) => {
  const request2 = new Request(
    `${BASE_URL$1}/export-visualization`,
    toRequestOptions(Method.POST, data)
  );
  return fetchJson(request2);
};
const linkDocument = (documentId, payload) => {
  const request2 = new Request(
    `${BASE_URL$1}/document/${documentId}/link`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const settings = () => {
  const request2 = new Request(
    `${BASE_URL$1}/settings`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const analyzeDocument = (documentId, payload) => {
  const request2 = new Request(
    `${BASE_URL$1}/document/${documentId}/analyze`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const integrationsLucidChartApi = {
  embedToken,
  pickerToken,
  exportVisualization,
  linkDocument,
  settings,
  analyzeDocument
};
const fetchAll$2 = async () => {
  const request2 = new Request("/api/invitation", {
    headers
  });
  return fetchJson(request2);
};
const create$4 = async (invitations) => {
  const request2 = new Request(
    "/api/invitation/bulk-invite",
    toRequestOptions(Method.POST, invitations)
  );
  return fetchJson(request2);
};
const update$4 = async (invitation) => {
  const request2 = new Request(
    `/api/invitation/${invitation._id}`,
    toRequestOptions(Method.PUT, invitation)
  );
  return fetchJson(request2);
};
const deleteInvitation = async (invitationId) => {
  const request2 = new Request(
    `/api/invitation/${invitationId}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoidAndRespondWithId(request2, invitationId);
};
const resend = async (invitationId) => {
  const request2 = new Request(
    `/api/invitation/${invitationId}/resend`,
    toRequestOptions(Method.POST, {})
  );
  return fetchVoid(request2);
};
const invitationApi = {
  fetchAll: fetchAll$2,
  create: create$4,
  update: update$4,
  delete: deleteInvitation,
  resend
};
const JOB_API = "/api/integrations/job";
async function fetchSchedulesList() {
  const request2 = new Request(JOB_API, { headers });
  return fetchJson(request2);
}
async function updateSchedule(schedule) {
  const request2 = new Request(
    `${JOB_API}/${schedule._id}`,
    toRequestOptions(Method.PUT, schedule)
  );
  return fetchJson(request2);
}
async function createSchedule(schedule) {
  const request2 = new Request(JOB_API, toRequestOptions(Method.POST, schedule));
  return fetchJson(request2);
}
async function pauseSchedule(scheduleId) {
  const request2 = new Request(
    `${JOB_API}/${scheduleId}/pause`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
}
async function deleteSchedule(schedule) {
  const request2 = new Request(
    `${JOB_API}/${schedule._id}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchJson(request2);
}
async function runSchedule(scheduleId) {
  const request2 = new Request(
    `${JOB_API}/${scheduleId}/run`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
}
const jobApi = {
  fetchSchedulesList,
  updateSchedule,
  createSchedule,
  deleteSchedule,
  runSchedule,
  pauseSchedule
};
const logApi = {
  post: createEntity("log")
};
const loginsByEmail = async (email) => {
  const request2 = new Request(
    "/api/login-service/find-logins",
    toRequestOptions(Method.POST, { email })
  );
  return fetchJson(request2);
};
const loginServiceApi = {
  loginsByEmail
};
const fetchAllMembers = (orgId) => {
  const request2 = new Request(`/api/organization/${orgId}/users`, {
    headers
  });
  return fetchJson(request2);
};
const fetchAllUsers = (orgId) => {
  const request2 = new Request(`/api/organization/${orgId}/all-users`, {
    headers
  });
  return fetchJson(request2);
};
const exportAllUsersExcel = (orgId) => {
  const request2 = new Request(`/api/organization/${orgId}/all-users/export`, {
    headers
  });
  return fetchBlob(request2);
};
const revokeMembership = async (orgId, userId) => {
  const request2 = new Request(
    `/api/organization/${orgId}/users/${userId}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
};
const updateRole = async (orgId, userId, role) => {
  const request2 = new Request(
    `/api/organization/${orgId}/users/${userId}`,
    toRequestOptions(Method.PUT, { _id: userId, role })
  );
  return fetchJson(request2);
};
const membershipApi = {
  exportAllUsersExcel,
  /**
   * @deprecated Use fetchAllUsers instead.
   *
   * Fetch all member in an organization (excluding contributors).
   */
  fetchAllMembers,
  /**
   * Fetch all users in an organization including contributors.
   */
  fetchAllUsers,
  revokeMembership,
  updateRole
};
const generate$1 = (workspaceIds, subdivisionIds) => {
  const request2 = new Request(
    "/api/metamodel/generate",
    toRequestOptions(Method.POST, {
      workspaceIds,
      subdivisionIds
    })
  );
  return fetchJson(request2);
};
const create$3 = (metamodel) => {
  const request2 = new Request(
    "/api/metamodel",
    toRequestOptions(Method.POST, metamodel)
  );
  return fetchJson(request2);
};
const update$3 = (_id, metamodel) => {
  const request2 = new Request(
    `/api/metamodel/${_id}`,
    toRequestOptions(Method.PUT, { _id, ...metamodel })
  );
  return fetchJson(request2);
};
const copy$7 = (metamodel, newName) => {
  return metamodelApi.create({
    name: newName,
    workspaceIds: metamodel.workspaceIds
  });
};
const metamodelApi = {
  generate: generate$1,
  fetchAllAssets: fetchEntities("metamodel/assets"),
  fetchAll: fetchEntities("metamodel"),
  create: create$3,
  update: update$3,
  fetch: fetchEntity("metamodel"),
  delete: deleteEntity("metamodel"),
  copy: copy$7
};
const fetch$1 = () => {
  const request2 = new Request("/api/metamodel2", { headers });
  return fetchJson(request2);
};
const metamodel2Api = {
  fetch: fetch$1
};
const BASE_API_URL$2 = `/api/integrations/microsoftgraph`;
async function fetchConsent() {
  const request2 = new Request(`${BASE_API_URL$2}/consent`, { headers });
  return fetchJson(request2);
}
const microsoftGraphApi = {
  fetchConsent
};
const BASE_API_URL$1 = `/api/integrations/tabular/microsoftgraph`;
const CONNECTIONS_API = "/api/integrations/microsoftgraph/connections";
const importPhotos = ({
  async,
  requestId,
  sourceId,
  transferConfig
}) => {
  const URL2 = `${BASE_API_URL$1}/source/${sourceId}/import-photos?async=${async}&requestId=${requestId}`;
  const request2 = new Request(
    URL2,
    toRequestOptions(Method.POST, transferConfig)
  );
  return fetchVoid(request2);
};
async function fetchConnectionsList$2() {
  const request2 = new Request(CONNECTIONS_API, { headers });
  return fetchJson(request2);
}
const microsoftGraphIntegrationApi = {
  importPhotos,
  fetchConnectionsList: fetchConnectionsList$2
};
const fetchAdvancedSearch = async () => {
  const request2 = new Request(
    `/api/migration-checklist/advanced-search-postgres`,
    { headers }
  );
  return fetchJson(request2);
};
const migrationChecklistApi = {
  fetchAdvancedSearch
};
const getUsage = (modelId) => {
  const request2 = new Request(`/api/model/${modelId}/usage`, {
    headers
  });
  return fetchJson(request2);
};
const referenceTypesInUse = (modelId) => {
  const request2 = new Request(`/api/model/${modelId}/reference-types-in-use`, {
    headers
  });
  return fetchJson(request2);
};
const modelApi = {
  getUsage,
  update: updateEntity("model"),
  referenceTypesInUse,
  fetch: fetchEntity("model"),
  fetchAll: fetchEntities("model")
};
const fetchAll$1 = () => {
  const request2 = new Request(`/api/notifications`, { headers });
  return fetchJson(request2);
};
const markNotifications = (payload) => {
  const request2 = new Request(
    `/api/notifications/mark`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const notificationApi = {
  fetchAll: fetchAll$1,
  markNotifications
};
const fetchOrganizationLogo = (organizationId) => {
  const request2 = new Request(`/api/organization/${organizationId}/logo`, {
    headers
  });
  return fetchJson(request2);
};
const fetchUsers = async (orgId) => {
  const request2 = new Request(`/api/organization/${orgId}/users`, { headers });
  return fetchJson(request2);
};
const fetchUserById = async (userId, organizationId) => {
  const users = organizationId ? await fetchUsers(organizationId) : [];
  if (isArdoqError(users)) {
    return users;
  }
  const user = users.find(({ _id }) => _id === userId);
  if (!user) {
    throw new Error("User not found");
  }
  return user;
};
const updateOrganization = async (attributes) => {
  const organization = await fetchJson(
    new Request(
      `/api/organization/${attributes._id}`,
      toRequestOptions(Method.PUT, attributes)
    )
  );
  if (isArdoqError(organization)) {
    return organization;
  }
  const consent = await fetchVoid(
    new Request(
      "/api/organization/consent/viewdata",
      toRequestOptions(Method.PUT, {
        hasConsentedToViewData: attributes.hasConsentedToViewData ?? false
      })
    )
  );
  if (isArdoqError(consent)) {
    return consent;
  }
  return organization;
};
const updateUserManagementSettings = async (payload) => {
  return fetchVoid(
    new Request(
      "/api/organization/settings/user-management",
      toRequestOptions(Method.PUT, payload)
    )
  );
};
const fetchLoginOptions = async () => {
  const request2 = new Request(`/api/organization/settings/login`, { headers });
  return fetchJson(request2);
};
const updateLoginOptionName = async (id, name) => {
  return fetchJson(
    new Request(
      `/api/organization/settings/login/${id}`,
      toRequestOptions(Method.PUT, {
        loginOptionName: name
      })
    )
  );
};
const organizationApi = {
  fetchOrganizationLogo,
  fetchUsers,
  fetch: fetchEntity("organization"),
  fetchAll: fetchEntities("organization"),
  updateOrganization,
  updateUserManagementSettings,
  fetchUserById,
  fetchLoginOptions,
  updateLoginOptionName
};
const fetchGlobalFields = () => {
  const request2 = new Request(`/api/metamodel-sync/global-fields`, {
    headers
  });
  return fetchJson(request2);
};
const updateGlobalField = (globalField) => {
  const request2 = new Request(
    `/api/metamodel-sync/global-field/${globalField._id}`,
    toRequestOptions(Method.PUT, globalField)
  );
  return fetchJson(request2);
};
const fetchMetamodel = (workspaceId) => {
  const request2 = new Request(`/api/metamodel-sync/${workspaceId}`, {
    headers
  });
  return fetchJson(request2);
};
const updateMetamodel = (metamodel) => {
  const request2 = new Request(
    `/api/metamodel-sync/${metamodel.workspaceId}`,
    toRequestOptions(Method.PUT, metamodel)
  );
  return fetchJson(request2);
};
const testGremlin = (body) => {
  const request2 = new Request(
    "/api/metamodel-sync/test-gremlin",
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const metamodelSyncApi = {
  fetchGlobalFields,
  updateGlobalField,
  fetchMetamodel,
  updateMetamodel,
  testGremlin
};
const updateResourcePermission = ({
  resourceType,
  resourceId,
  permission
}) => {
  const request2 = new Request(
    `/api/permission/${resourceType}/${resourceId}`,
    toRequestOptions(Method.PUT, permission)
  );
  return fetchJson(request2);
};
const updateMultipleResourcePermissions = async (permissions) => {
  const requests = permissions.map(
    (permission) => updateResourcePermission({
      resourceType: permission["resource-type"],
      resourceId: permission.resource,
      permission
    })
  );
  const results = await Promise.all(requests);
  const resultList = [];
  for (const result of results) {
    if (isArdoqError(result)) {
      return result;
    }
    resultList.push(result);
  }
  return resultList;
};
const getResourcePermission = ({
  resourceType,
  resourceId
}) => {
  const request2 = new Request(`/api/permission/${resourceType}/${resourceId}`, {
    headers
  });
  return fetchJson(request2);
};
const fetchPermissionsForAllZones = () => {
  const request2 = new Request("/api/permission/permission-zone", {
    headers
  });
  return fetchJson(request2);
};
const fetchUserPermissionsOverview = ({
  userId,
  queryParams
}) => {
  const queryParamsPairs = Object.entries(queryParams).filter(([_, value]) => value.length > 0).map(([key, value]) => [kebabCase(key), value.join(",")]);
  const params = queryParamsPairs.length > 0 ? `?${new URLSearchParams(queryParamsPairs).toString()}` : "";
  const request2 = new Request(
    `/api/permission/permissions-overview/user/${userId}${params}`,
    {
      headers
    }
  );
  return fetchJson(request2);
};
const fetchOrgPermissionsOverview = () => {
  const request2 = new Request(
    "/api/permission/permissions-overview/assets/all"
  );
  return fetchJson(request2);
};
const sendBatchUpdate = (payload) => {
  const request2 = new Request("/api/permission/batch", {
    method: Method.POST,
    headers,
    body: JSON.stringify(payload)
  });
  return fetchVoid(request2);
};
const permissionApi = {
  updateResourcePermission,
  updateMultipleResourcePermissions,
  getResourcePermission,
  fetchPermissionsForAllZones,
  fetchUserPermissionsOverview,
  fetchOrgPermissionsOverview,
  sendBatchUpdate
};
const updateZonesWithPermissions = (zonesWithPermissions) => {
  const request2 = new Request(
    "/api/permission/zone/update/many",
    toRequestOptions(Method.PUT, { updates: zonesWithPermissions })
  );
  return fetchJson(request2);
};
const permissionZoneApi = {
  ...crudApi("permission/zone"),
  updateZonesWithPermissions
};
const fetchAggregated$1 = (presentationId) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/aggregated`,
    { headers }
  );
  return fetchJson(request2);
};
const migrationStatus = (presentationId, slideIds) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/slide-migration-status`,
    toRequestOptions(Method.POST, { slideIds })
  );
  return fetchJson(request2);
};
const fetchSlides = async (presentationId) => {
  const request2 = new Request(`/api/presentation/${presentationId}/slides`, {
    headers
  });
  return fetchJson(request2);
};
const copy$6 = (presentationId, name) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/copy`,
    toRequestOptions(Method.POST, { name })
  );
  return fetchJson(request2);
};
const search$2 = async (presentationId, reportId, slideId, body) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/slide/${slideId}/report/${reportId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const fetchReportColumnSuggestions = (presentationId, reportId, slideId, columnKey, query, limit = 100) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/slide/${slideId}/report/${reportId}/column/${columnKey}/suggestion?q=${query}&limit=${limit}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const fetchDashboard = async (presentationId, dashboardId) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/dashboard/v3/${dashboardId}`,
    { headers }
  );
  return fetchJson(request2);
};
const addSlide = async (presentationId, slide) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/add-slide/`,
    toRequestOptions(Method.POST, slide)
  );
  return fetchJson(request2);
};
const fetchReportAggregates = (presentationId, slideId, reportId, filters) => {
  const request2 = new Request(
    `/api/presentation/${presentationId}/slide/${slideId}/report/${reportId}/aggregates`,
    toRequestOptions(Method.POST, { filters })
  );
  return fetchJson(request2);
};
const presentationApi = {
  fetchAggregated: fetchAggregated$1,
  migrationStatus,
  fetchSlides,
  copy: copy$6,
  search: search$2,
  fetchReportColumnSuggestions,
  addSlide,
  fetchDashboard,
  rename: renameEntity("presentation"),
  fetchReportAggregates,
  ...crudApi("presentation")
};
const fetchConfigurablePrivileges = () => {
  const request2 = new Request("/api/privilege/list-configurable", { headers });
  return fetchJson(request2);
};
const fetchAllUsersPrivileges = () => {
  const request2 = new Request("/api/privilege/user", { headers });
  return fetchJson(request2);
};
const update$2 = ({
  userId,
  privileges
}) => {
  const request2 = new Request(
    `/api/privilege/user`,
    toRequestOptions(Method.POST, { [userId]: privileges })
  );
  return fetchVoid(request2);
};
const privilegeApi = {
  fetchConfigurablePrivileges,
  fetchAllUsersPrivileges,
  update: update$2
};
const scopedata = async (entityIds) => {
  const request2 = new Request(
    `/api/reference/scopedata`,
    toRequestOptions(Method.POST, { "reference-ids": entityIds })
  );
  return fetchJson(request2).then(
    (result) => {
      if (isArdoqError(result)) {
        return result;
      }
      return { permissions: [], ...result };
    }
  );
};
const merge = ({
  mergedEntity,
  entityIdsToDelete
}) => {
  const request2 = new Request(
    `/api/reference/${mergedEntity._id}/merge`,
    toRequestOptions(Method.PUT, {
      "subsumed-reference-ids": entityIdsToDelete,
      "merged-reference-data": mergedEntity
    })
  );
  return fetchJson(request2);
};
const restore = async (payload) => {
  const entityAttributes = await referenceApi.fetch(payload.entityId);
  if (isArdoqError(entityAttributes)) {
    return entityAttributes;
  }
  return referenceApi.updateReference({
    ...entityAttributes,
    [payload.fieldName]: payload.restorableValue
  });
};
const getReferenceTypesSortedByMostUsed = (payload) => {
  const request2 = new Request(
    `/api/reference/sorted-type-suggestions`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const referenceApi = {
  updateReference: updateEntity("reference"),
  fetch: fetchEntity("reference"),
  scopedata,
  merge,
  restore,
  getReferenceTypesSortedByMostUsed
};
const referenceTypeApi = {
  fetchAll: fetchEntities("reference-type")
};
const exportReport = async (id, params) => {
  const request2 = new Request(
    `/api/report/${id}/export`,
    toRequestOptions(Method.POST, params)
  );
  return fetchBlob(request2);
};
const exportReportExcel = async (id, params) => {
  const request2 = new Request(
    `/api/report/${id}/export-excel`,
    toRequestOptions(Method.POST, params)
  );
  return fetchBlob(request2);
};
const usage$1 = (reportIds) => {
  const request2 = new Request(
    `/api/report/usage`,
    toRequestOptions(Method.POST, { reportIds })
  );
  return fetchJson(request2);
};
const search$1 = ({
  query,
  reportId
}) => {
  const url = new URL(
    reportId ? `/api/report/${reportId}/search` : `/api/report/search`,
    window.location.origin
  );
  const request2 = new Request(url, toRequestOptions(Method.POST, query));
  return fetchJson(request2);
};
const searchAggregates = ({
  query,
  reportId
}) => {
  const url = new URL(
    reportId ? `/api/report/${reportId}/search/aggregates` : `/api/report/search/aggregates`,
    window.location.origin
  );
  const request2 = new Request(url, toRequestOptions(Method.POST, query));
  return fetchJson(request2);
};
const create$2 = (report) => {
  const request2 = new Request(
    "/api/report/create-query-and-report",
    toRequestOptions(Method.POST, report)
  );
  return fetchJson(request2);
};
const update$1 = (report, keepHistoricalData) => {
  const url = new URL(
    `/api/report/${report._id}/update-query-and-report`,
    window.location.origin
  );
  url.searchParams.set("discardHistoricalData", String(!keepHistoricalData));
  const request2 = new Request(url, toRequestOptions(Method.PUT, report));
  return fetchJson(request2);
};
const deleteReport = (id) => {
  const request2 = new Request(
    `/api/report/${id}/delete-query-and-report`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchAndRespondWith(request2, id);
};
const reportWithHistoricalData = (id) => {
  const url = new URL(`/api/report/${id}`, window.location.origin);
  url.searchParams.set("includeHasHistoricalData", "true");
  const request2 = new Request(url, { headers });
  return fetchJson(request2);
};
const drilldown = (id, from, size, sort, order, filters) => {
  const request2 = new Request(
    `/api/report/${id}/drilldown`,
    toRequestOptions(Method.POST, {
      id,
      from,
      size,
      sort,
      order,
      filters
    })
  );
  return fetchJson(request2);
};
const getColumnSuggestions = (reportId, columnKey, query, limit = 100) => {
  const request2 = new Request(
    `/api/report/${reportId}/column/${columnKey}/suggestion?q=${query}&limit=${limit}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const copy$5 = (id, name) => {
  const request2 = new Request(
    `/api/report/copy`,
    toRequestOptions(Method.POST, { id, name })
  );
  return fetchJson(request2);
};
const fetchAggregates = (reportId, filters) => {
  const request2 = new Request(
    `/api/report/${reportId}/aggregates`,
    toRequestOptions(Method.POST, { filters })
  );
  return fetchJson(request2);
};
const generateDescription$2 = async (body) => {
  const request2 = new Request(
    `/api/report/description/autocomplete`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const reportApi = {
  copy: copy$5,
  create: create$2,
  delete: deleteReport,
  drilldown,
  exportReport,
  exportReportExcel,
  fetch: fetchEntity("report"),
  fetchAll: fetchEntities("report"),
  getColumnSuggestions,
  rename: renameEntity("report"),
  reportWithHistoricalData,
  search: search$1,
  searchAggregates,
  fetchAggregates,
  update: update$1,
  usage: usage$1,
  generateDescription: generateDescription$2
};
const request = async (accessRequest) => {
  const request2 = new Request(
    "/api/request-access",
    toRequestOptions(Method.POST, accessRequest)
  );
  const response = await fetchJson(request2);
  if (isArdoqError(response)) return response;
  return accessRequest.requests.map(({ resourceId }) => resourceId);
};
const details = async (id) => {
  const request2 = new Request(`/api/request-access/${id}/details`, { headers });
  const response = await fetchJson(request2);
  if (isArdoqError(response)) {
    return response;
  }
  return { response, id };
};
const approve = ({
  id,
  resolutions
}) => {
  const request2 = new Request(
    `/api/request-access/${id}/approve`,
    toRequestOptions(Method.POST, { resolutions })
  );
  return fetchJson(request2);
};
const deny = ({
  id
}) => {
  const request2 = new Request(
    `/api/request-access/${id}/deny`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
};
const resolve = ({
  id,
  resolutions
}) => {
  if (resolutions) {
    return approve({ id, resolutions });
  }
  return deny({ id });
};
const requestAccessApi = {
  request,
  details,
  resolve
};
const component = ({
  workspace: workspace2,
  component: component2
}) => {
  const request2 = new Request(
    "/api/restore/component?restoreDescendants=true",
    toRequestOptions(Method.POST, { workspace: workspace2, component: component2 })
  );
  return fetchVoid(request2);
};
const workspace = (workspaceId) => {
  const request2 = new Request(`/api/restore/${workspaceId}/deleted`, {
    headers
  });
  return fetchJson(request2);
};
const reference = ({
  workspace: workspace2,
  reference: reference2
}) => {
  const request2 = new Request(
    "/api/restore/reference?restoreDescendants=true",
    toRequestOptions(Method.POST, { workspace: workspace2, reference: reference2 })
  );
  return fetchVoid(request2);
};
const restoreApi = {
  component,
  workspace,
  reference
};
const fetchAll = () => {
  const request2 = new Request(`/api/role`, { headers });
  return fetchJson(request2);
};
const update = (role) => {
  const request2 = new Request(`/api/role`, toRequestOptions(Method.PUT, role));
  return fetchJson(request2);
};
const roleApi = {
  fetchAll,
  update
};
const deleteComponents = (scenarioId, componentIds) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/component`,
    toRequestOptions(Method.DELETE, { componentIds })
  );
  return fetchJson(request2);
};
const deleteExternalDocument = (scenarioId, externalDocumentId) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/external-document/${externalDocumentId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchVoid(request2);
};
const removeWorkspace = async ({
  scenarioId,
  workspaceId
}) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/remove-workspace-from-scenario`,
    toRequestOptions(Method.PUT, { workspaceId })
  );
  return fetchVoidAndRespondWithId(request2, scenarioId);
};
const subtreeInfo = async (scenarioId, componentIds) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/component/subtree-info`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson(request2);
};
const updateBranchOff = async (scenarioId) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/update-branch-off`,
    toRequestOptions(Method.PUT, null)
  );
  return fetchVoid(request2);
};
const isInSync = async (scenarioId) => {
  const request2 = new Request(`/api/scenario/${scenarioId}/insync`, {
    headers
  });
  return fetchJson(request2);
};
const canAddToScenario = (scenarioId, componentIds) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/can-add`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson(request2);
};
const addToScenario = (payload) => {
  const request2 = new Request(
    `/api/scenario/${payload.scenarioId}/add-to-scenario`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson(request2);
};
const removeFromScenario = (payload) => {
  const request2 = new Request(
    `/api/scenario/${payload.scenarioId}/remove-from-scenario`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson(request2);
};
const create$1 = (scenarioAttributes) => {
  const request2 = new Request(
    "/api/scenario/create",
    toRequestOptions(Method.POST, scenarioAttributes)
  );
  return fetchJson(request2);
};
const copy$4 = (scenarioId, name) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/copy`,
    toRequestOptions(Method.POST, { name })
  );
  return fetchJson(request2);
};
const getScenarioReferences = (componentIds, scopeId, branchId) => {
  const urlParams = new URLSearchParams();
  if (scopeId && branchId) {
    urlParams.set("scopeId", scopeId);
    urlParams.set("branchId", branchId);
  }
  const request2 = new Request(
    `/api/scope/references?${urlParams.toString()}`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson(request2);
};
const getNumberOfRelationsOutsideScope = (scenarioId, componentIds) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/number-of-relations-outside-scope`,
    toRequestOptions(Method.POST, { componentIds })
  );
  return fetchJson(request2);
};
const load = (scenarioId) => {
  const request2 = new Request(`/api/scenario/${scenarioId}/aggregated/branch`, {
    headers
  });
  return fetchJson(request2);
};
const diffContext = (scenarioId) => {
  const request2 = new Request(`/api/scenario/${scenarioId}/diffcontext`, {
    headers
  });
  return fetchJson(request2);
};
const reset = (scenarioId, body, branchId) => {
  const url = new URL(
    `/api/scenario/${scenarioId}/reset`,
    window.location.origin
  );
  if (branchId) {
    url.searchParams.set("branchId", branchId);
  }
  const request2 = new Request(url, toRequestOptions(Method.POST, body));
  return fetchJson(request2);
};
const mergeEnitites = ({
  mergedEntity,
  entityIdsToDelete,
  scenarioId
}) => {
  if (getEntityType(mergedEntity) === APIEntityType.COMPONENT) {
    return mergeComponents({
      mergedEntity,
      entityIdsToDelete,
      scenarioId
    });
  }
  return mergeReferences({
    mergedEntity,
    entityIdsToDelete,
    scenarioId
  });
};
const mergeComponents = ({
  mergedEntity,
  entityIdsToDelete,
  scenarioId
}) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/component/${mergedEntity._id}/merge`,
    toRequestOptions(Method.PUT, {
      "subsumed-component-ids": entityIdsToDelete,
      "merged-component-data": mergedEntity
    })
  );
  return fetchJson(request2);
};
const updateComponent = ({
  component: component2,
  scenarioId
}) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/component/${component2._id}`,
    toRequestOptions(Method.PUT, component2)
  );
  return fetchJson(request2);
};
const mergeReferences = ({
  mergedEntity,
  entityIdsToDelete,
  scenarioId
}) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/reference/${mergedEntity._id}/merge`,
    toRequestOptions(Method.PUT, {
      "subsumed-reference-ids": entityIdsToDelete,
      "merged-reference-data": mergedEntity
    })
  );
  return fetchJson(request2);
};
const createComponent = ({
  component: component2,
  scenarioId
}) => {
  const request2 = new Request(
    `/api/scenario/${scenarioId}/component`,
    toRequestOptions(Method.POST, component2)
  );
  return fetchJson(request2);
};
const scenarioApi = {
  deleteComponents,
  deleteExternalDocument,
  removeWorkspace,
  updateBranchOff,
  isInSync,
  canAddToScenario,
  getScenarioReferences,
  getNumberOfRelationsOutsideScope,
  subtreeInfo,
  create: create$1,
  copy: copy$4,
  load,
  diffContext,
  reset,
  addToScenario,
  removeFromScenario,
  mergeEnitites,
  fetchAll: fetchEntities("scenario"),
  rename: renameEntity("scenario"),
  delete: deleteEntity("scenario"),
  createComponent,
  updateComponent
};
const exists = async () => {
  const request2 = new Request("/api/scim/v2/Token", { headers });
  return fetchJson(request2);
};
const generate = async () => {
  const request2 = new Request(
    "/api/scim/v2/Token",
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
};
const deleteToken = async () => {
  const request2 = new Request(
    "/api/scim/v2/Token",
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
};
const scimApi = {
  exists,
  generate,
  delete: deleteToken
};
const createUrl = (query, { types, workspaceIds, customFieldName, from, size }) => {
  return [
    `/api/search?q=${encodeURIComponent(query)}`,
    types && `&types=${types}`,
    workspaceIds && `&workspaceIds=${workspaceIds}`,
    customFieldName && `&customFieldName=${customFieldName}`,
    from && `&from=${from}`,
    size && `&size=${size}`
  ].filter(isTruthy).join("");
};
const customSearch = (query, options) => {
  const request2 = new Request(createUrl(query, options), { headers });
  return fetchJson(request2);
};
const newSearch = ({
  query,
  disableArdoqIdSearch,
  customSearchField,
  size,
  workspaceIds
}) => {
  const request2 = new Request(`/api/viewpoint/resource/component/search`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      q: query,
      disableArdoqIdSearch,
      customSearchField,
      size,
      workspaceIds: workspaceIds ?? []
    })
  });
  return fetchJson(request2);
};
const searchApi = { customSearch, newSearch };
const fetchServiceAccounts = () => {
  const request2 = new Request("/api/user/service-account", { headers });
  return fetchJson(request2);
};
const createServiceAccount = (name, role) => {
  const request2 = new Request(
    "/api/user/service-account",
    toRequestOptions(Method.POST, { name, role })
  );
  return fetchJson(request2);
};
const createTokenForServiceAccount = (userId, description) => {
  const request2 = new Request(
    "/api/token/org/service-account-token",
    toRequestOptions(Method.POST, {
      "user-id": userId,
      type: "api",
      description
    })
  );
  return fetchJson(request2);
};
const createServiceAccountAndToken = ({
  name,
  role,
  tokenDescription
}) => {
  const request2 = new Request(
    "/api/user/service-account-and-token",
    toRequestOptions(Method.POST, { name, role, tokenDescription })
  );
  return fetchJson(request2);
};
const deleteServiceAccount = async (id) => {
  const request2 = new Request(
    `/api/user/service-account`,
    toRequestOptions(Method.DELETE, { userId: id })
  );
  return fetchVoidAndRespondWithId(request2, id);
};
const serviceAccountApi = {
  fetchServiceAccounts,
  createServiceAccount,
  createTokenForServiceAccount,
  createServiceAccountAndToken,
  deleteServiceAccount
};
const servicenowConfigApi = {
  fetchAll: fetchEntities(
    "integrations/servicenow/config"
  )
};
const BASE_URL = "/api/integrations/servicenow";
const fetchLegacyConfigs = async () => {
  const request2 = new Request(`${BASE_URL}/config`, {
    headers
  });
  return fetchJson(request2);
};
const fetchSelection = async ({
  selection,
  requestId,
  async
}) => {
  const URL2 = `/api/integrations/tabular/servicenow?async=${async}&requestId=${requestId}`;
  const request2 = new Request(URL2, toRequestOptions(Method.POST, selection));
  return fetchIntegrationJson(request2);
};
const fetchTableFields = async ({
  tableId,
  connectionId
}) => {
  const URL2 = `${BASE_URL}/connection/${connectionId}/table/${tableId}/field`;
  const request2 = new Request(URL2, { headers });
  return fetchIntegrationJson(request2);
};
const fetchTableList = async ({
  connectionId,
  requestId,
  async
}) => {
  const URL2 = `${BASE_URL}/connection/${connectionId}/table?&async=${async}&requestId=${requestId}`;
  const request2 = new Request(URL2, { headers });
  return fetchIntegrationJson(request2);
};
async function fetchConnectionsList$1() {
  const request2 = new Request(`${BASE_URL}/connection`, { headers });
  return fetchJson(request2);
}
async function pingConnection(payload) {
  const request2 = new Request(
    `${BASE_URL}/ping`,
    toRequestOptions(Method.POST, payload)
  );
  return fetch(request2);
}
async function createConnection$1(payload) {
  const request2 = new Request(
    `${BASE_URL}/connection`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchIntegrationJson(request2);
}
async function deleteConnection$1(connection) {
  const request2 = new Request(
    `${BASE_URL}/connection/${connection._id}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
}
async function updateConnection$1(id, payload) {
  const request2 = new Request(
    `${BASE_URL}/connection/${id}`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchVoid(request2);
}
const servicenowIntegrationApi = {
  fetchLegacyConfigs,
  fetchSelection,
  fetchTableFields,
  fetchTableList,
  fetchConnectionsList: fetchConnectionsList$1,
  pingConnection,
  createConnection: createConnection$1,
  updateConnection: updateConnection$1,
  deleteConnection: deleteConnection$1
};
const BASE_API_URL = "/api/integrations/signavio-export";
function fetchDictionaries(connectionId) {
  const request2 = new Request(
    `${BASE_API_URL}/${connectionId}/signavio/dictionaries`,
    {
      headers
    }
  );
  return fetchIntegrationJson(request2);
}
function postExport(payload, requestId, dryRun) {
  const request2 = new Request(
    `${BASE_API_URL}/export?dryRun=${String(
      dryRun
    )}&async=true&requestId=${requestId}`,
    toRequestOptions(Method.POST, payload)
  );
  return fetch(request2).then(
    toApiResponse
  ).catch((error) => handleNetworkError(request2, error));
}
function postScheduleExport(jobOptions) {
  const request2 = new Request(
    `${BASE_API_URL}/schedule-job`,
    toRequestOptions(Method.POST, jobOptions)
  );
  return fetch(request2).then(toApiResponse).catch((error) => handleNetworkError(request2, error));
}
const signavioExporterApi = {
  fetchDictionaries,
  postExport,
  postScheduleExport
};
const slideApi = {
  ...crudApi("slide")
};
const postPreview = (body) => {
  const request2 = new Request(
    "/api/sso-mapping/preview/preview",
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const fetchAggregated = () => {
  const request2 = new Request(
    `/api/sso-mapping/aggregated/entries`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const SSOMappingApi = {
  ...crudApi("sso-mapping"),
  postPreview,
  fetchAggregated
};
const storedQueryApi = {
  ...crudApi("stored-query")
};
const isSuccess = (x) => !isArdoqError(x);
const handleError = (errorHandler = logError) => pipe(
  catchError((error) => of(toArdoqError({ error }))),
  tap((error) => {
    if (isArdoqError(error)) {
      errorHandler(error);
    }
  }),
  filter(isSuccess)
);
const logWithMessage = (message) => (error) => {
  logError(error, message);
};
const updateComponents = (payload, action) => {
  const request2 = new Request(
    `/api/subdivision/component/${action}`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const addComponents = (payload) => {
  return updateComponents(
    payload,
    "add"
    /* ADD */
  );
};
const addResources = (payload) => {
  const request2 = new Request(
    "/api/subdivision/workspace/add",
    toRequestOptions(Method.POST, {
      [payload.subdivisionId]: payload.resourceIds
    })
  );
  return fetchJson(request2);
};
const removeComponents = (payload) => {
  return updateComponents(
    payload,
    "remove"
    /* REMOVE */
  );
};
const componentsToBeAddedApi = (payload) => {
  const request2 = new Request(
    `/api/subdivision/${payload.subdivisionId}/staged-changes`,
    toRequestOptions(Method.POST, payload.query)
  );
  return fetchJson(request2);
};
const setCreationContext = (payload) => {
  const request2 = new Request(
    "/api/subdivision/context/set-creation-context",
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const fetchSummary = (subdivisionId) => {
  const request2 = new Request(
    `/api/subdivision/${subdivisionId}/summary`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const fetchSimplifiedSummary = (subdivisionId) => {
  const request2 = new Request(
    `/api/subdivision/${subdivisionId}/summary?simplified=true`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const subdivisionApi = {
  ...crudApi("subdivision"),
  addComponents,
  addResources,
  removeComponents,
  componentsToBeAddedApi,
  setCreationContext,
  fetchSummary,
  fetchSimplifiedSummary
};
const getQuery = (suggestionType, name, workspaceIds, limit = 100) => {
  const request2 = new Request(
    `/api/suggestion/${suggestionType}?q=${encodeURIComponent(
      name
    )}&limit=${limit}`,
    toRequestOptions(Method.POST, { workspaceIds })
  );
  return fetchJson(request2);
};
const getReportSuggestionQuery = (suggestionType, name, workspaceIds, subdivisions) => {
  const url = new URL(
    `/api/report/search/suggestion/${suggestionType}`,
    window.origin
  );
  if (name) {
    url.searchParams.set("q", name);
  }
  const request2 = new Request(
    url,
    toRequestOptions(Method.POST, { workspaceIds, subdivisions })
  );
  return fetchJson(request2);
};
const getComponentSuggestionsForReferenceCreation = (referenceTypeName, searchString) => {
  const request2 = new Request(
    `/api/suggestion/component?q=${encodeURIComponent(searchString)}&limit=100`,
    toRequestOptions(Method.POST, { referenceTypeName })
  );
  return fetchJson(request2);
};
const suggestionApi = {
  getQuery,
  getReportSuggestionQuery,
  getComponentSuggestionsForReferenceCreation
};
const fetchCurrentUser = () => {
  const request2 = new Request("/api/user/current_user", { headers });
  return fetchJson(request2);
};
const updateSettings = ({
  userId,
  settings: settings2
}) => {
  const request2 = new Request(
    `/api/user/${userId}/settings`,
    toRequestOptions(Method.PUT, settings2)
  );
  return fetchJson(request2);
};
var FavoriteAction = /* @__PURE__ */ ((FavoriteAction2) => {
  FavoriteAction2["ADD"] = "add";
  FavoriteAction2["REMOVE"] = "remove";
  return FavoriteAction2;
})(FavoriteAction || {});
const updateFavorite = ({
  action,
  userId,
  payload
}) => {
  const request2 = new Request(
    `/api/user/${userId}/favorite/${action}`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson(request2);
};
const changePassword = async (userId, values) => {
  const request2 = new Request(
    `/api/user/${userId}/password`,
    toRequestOptions(Method.PUT, values)
  );
  return fetchVoid(request2);
};
const triggerPasswordReset = (email) => {
  const request2 = new Request(
    `/api/user/reset_password/trigger`,
    toRequestOptions(Method.POST, { email })
  );
  return fetchJson(request2);
};
const changeEmail = async (userId, values) => {
  const request2 = new Request(
    `/api/user/${userId}/email`,
    toRequestOptions(Method.PUT, values)
  );
  return fetchVoid(request2);
};
const fetchCurrentUserPermissions = () => {
  const request2 = new Request("/api/user/current_user/permissions", {
    headers
  });
  return fetchJson(request2);
};
const getConsent = (userId) => {
  const request2 = new Request(`/api/user/${userId}/consent`, { headers });
  return fetchJson(request2);
};
const updateConsent = (userId, consent) => {
  const request2 = new Request(
    `/api/user/${userId}/consent`,
    toRequestOptions(Method.PUT, consent)
  );
  return fetchVoid(request2);
};
const search = (searchQuery, orgMembersOnly, limit = 100) => {
  const searchParams = new URLSearchParams({
    q: searchQuery,
    orgMembersOnly: orgMembersOnly.toString(),
    limit: limit.toString()
  });
  const request2 = new Request(`/api/user/search?${searchParams}`, {
    headers
  });
  return fetchJson(request2);
};
const bulkUpdate = (bulkUpdatePayload) => {
  const request2 = new Request(
    "/api/user/bulk-operation/submit",
    toRequestOptions(Method.POST, bulkUpdatePayload)
  );
  return fetchJson(request2);
};
const userApi = {
  fetchCurrentUser,
  fetchCurrentUserPermissions,
  updateSettings,
  changePassword,
  triggerPasswordReset,
  changeEmail,
  updateFavorite,
  getConsent,
  updateConsent,
  search,
  bulkUpdate
};
const createComponentFromAnswers = (surveyId, broadcastInstanceId, componentName, parentId, answers) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/answers/${broadcastInstanceId ? `?broadcastInstanceId=${broadcastInstanceId}` : ""}`,
    toRequestOptions(Method.POST, {
      component: {
        name: componentName,
        parent: parentId ?? void 0
      },
      answers
    })
  );
  return fetchJson(request2);
};
const deleteComponent = async (surveyId, componentId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/component/${componentId}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
};
const fetchAnswers = (surveyId, componentId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/answers/${componentId}`,
    { headers }
  );
  return fetchJson(request2);
};
const fetchComponentHierarchy = (surveyId) => {
  const request2 = new Request(`/api/survey/${surveyId}/hierarchy`, { headers });
  return fetchJson(request2);
};
const fetchComponents$1 = (surveyId, filter2, broadcastInstanceId, searchParams) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/get-components`,
    toRequestOptions(Method.POST, {
      filter: filter2 ?? void 0,
      from: searchParams.from,
      size: searchParams.size,
      sort: searchParams.sortBy,
      order: searchParams.sortOrder,
      broadcastInstanceId: broadcastInstanceId ?? void 0
    })
  );
  return fetchJson(request2);
};
const fetchFields = (surveyId) => {
  const request2 = new Request(`/api/survey/${surveyId}/fields`, { headers });
  return fetchJson(request2);
};
const fetchQuestionOptions = (surveyId, questionId, componentId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/question/${questionId}/options${componentId ? `?componentId=${componentId}` : ""}`,
    { headers }
  );
  return fetchJson(request2);
};
const fetchComponentOptionsWithQuery = ({
  surveyId,
  questionId,
  componentId,
  query
}) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/question/${questionId}/options`,
    toRequestOptions(Method.POST, {
      componentId,
      q: query
    })
  );
  return fetchJson(request2);
};
const fetchSurvey = (surveyId) => {
  const request2 = new Request(`/api/survey/${surveyId}`, { headers });
  return fetchJson(request2);
};
const fetchTags = (surveyId) => {
  const request2 = new Request(`/api/survey/${surveyId}/tags`, { headers });
  return fetchJson(request2);
};
const saveAnswers = (surveyId, broadcastInstanceId, componentId, answers) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/answers/${componentId}${broadcastInstanceId ? `?broadcastInstanceId=${encodeURIComponent(broadcastInstanceId)}` : ""}`,
    toRequestOptions(Method.PUT, {
      answers,
      component: componentId
    })
  );
  return fetchJson(request2);
};
const postSurveyNotificationUnsubscription = (surveyId, token, orgLabel) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/digest/unsubscribe?org=${orgLabel}&token=${token}`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson(request2);
};
const fetchResponseLogData = (surveyId, updateType) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/digest/history/${updateType}`,
    { headers }
  );
  return fetchJson(request2).then((response) => {
    if (isArdoqError(response)) return response;
    response.surveyId = surveyId;
    return response;
  });
};
const fetchPendingApprovalsSummary = () => {
  const request2 = new Request("/api/survey/approval-summary", { headers });
  return fetchJson(request2);
};
const fetchPendingApproval = (surveyId) => {
  const request2 = new Request(`/api/survey/${surveyId}/approval`, { headers });
  return fetchJson(request2);
};
const approvedChanges = (surveyId, componentId, approvedChanges2) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/component/${componentId}/approval`,
    toRequestOptions(Method.POST, approvedChanges2)
  );
  return fetchJson(request2);
};
const rejectChanges = (surveyId, componentId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/component/${componentId}/approval`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson(request2);
};
const resetAnswers = (surveyId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/approval/reset`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson(request2);
};
const changeApprovalDecision = (changeRequestId, decision, answers) => {
  const request2 = new Request(
    `/api/change-approval/request/${changeRequestId}`,
    toRequestOptions(Method.POST, { decision, answers })
  );
  return fetch(request2);
};
const resetInvalidChangeRequest = (surveyId, componentId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/component/${componentId}/reset`,
    toRequestOptions(Method.POST, null)
  );
  return fetchJson(request2);
};
const copy$3 = (survey, newName) => {
  const freshSurvey = omit(
    {
      ...survey,
      name: newName,
      published: false,
      questions: survey.questions.map((question) => omit(question, "_id"))
    },
    [
      "_id",
      "_version",
      "created",
      "createdBy",
      "createdByEmail",
      "createdByName",
      "lastModifiedBy",
      "lastUpdated",
      "lastModifiedByEmail",
      "lastModifiedByName",
      "ardoq",
      "ardoqPersistent",
      "meta",
      "rowType",
      "discoverPriority"
    ]
  );
  return surveyApi.create(freshSurvey);
};
const fetchSimilarSurveyComponents = (surveyId, name, broadcastInstanceId) => {
  const request2 = new Request(
    `/api/survey/${surveyId}/component-suggest`,
    toRequestOptions(Method.POST, { name, broadcastInstanceId })
  );
  return fetchJson(request2);
};
const fetchSurveyFormData = async ({
  surveyId,
  componentId
}) => {
  const [survey, fields, currentUser, answersData] = await Promise.all([
    surveyApi.fetch(surveyId),
    fetchFields(surveyId),
    userApi.fetchCurrentUser(),
    componentId ? fetchAnswers(surveyId, componentId) : null
  ]);
  if (isArdoqError(survey)) return survey;
  if (isArdoqError(fields)) return fields;
  if (isArdoqError(currentUser)) return currentUser;
  if (isArdoqError(answersData)) return answersData;
  return { survey, fields, currentUser, answersData };
};
const createWithPermissions$1 = (survey) => {
  const request2 = new Request(
    "/api/survey/create",
    toRequestOptions(Method.POST, survey)
  );
  return fetchJson(request2);
};
const createInFolder$1 = async (survey, folderId) => {
  const response = await createWithPermissions$1(survey);
  if (isArdoqError(response)) {
    return response;
  }
  const moveAssetsResponse = await workspaceFolderApi.moveAssets({
    targetFolderId: folderId,
    content: [{ _id: response.survey._id, type: AssetType.SURVEY }]
  });
  if (isArdoqError(moveAssetsResponse)) {
    return moveAssetsResponse;
  }
  return response;
};
const submitSurveyForm = (surveyId, body, broadcastInstanceId) => {
  const queryString = broadcastInstanceId ? `?broadcastInstanceId=${encodeURIComponent(broadcastInstanceId)}` : "";
  const request2 = new Request(
    `/api/survey/${surveyId}/answers/${queryString}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const fetchComponentSurveys = (componentId) => {
  const request2 = new Request(`/api/survey?componentId=${componentId}`, {
    headers
  });
  return fetchJson(request2);
};
const surveyApi = {
  approvedChanges,
  rejectChanges,
  fetchPendingApproval,
  fetchPendingApprovalsSummary,
  fetchResponseLogData,
  postSurveyNotificationUnsubscription,
  createComponentFromAnswers,
  deleteComponent,
  fetchAnswers,
  fetchComponentHierarchy,
  fetchComponents: fetchComponents$1,
  fetchFields,
  fetchQuestionOptions,
  fetchComponentOptionsWithQuery,
  fetchSurvey,
  fetchTags,
  saveAnswers,
  resetAnswers,
  changeApprovalDecision,
  resetInvalidChangeRequest,
  rename: renameEntity("survey"),
  copy: copy$3,
  fetchSimilarSurveyComponents,
  ...crudApi("survey"),
  fetchSurveyFormData,
  createWithPermissions: createWithPermissions$1,
  createInFolder: createInFolder$1,
  submitSurveyForm,
  fetchComponentSurveys
};
const tabularMappingApi = {
  fetchAll: fetchEntities(
    "integrations/tabular/mapping"
  )
};
const tagApi = {
  fetchTag: fetchEntity("tag"),
  fetchAll: fetchEntities("tag")
};
const templateApi = {
  create: createEntity("template"),
  update: updateEntity("template")
};
const create = async (payload) => {
  const request2 = new Request(
    "/api/token",
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
};
const tokenApi = {
  create,
  update: updateEntity("token"),
  delete: deleteEntity("token"),
  fetchAll: fetchEntities("token"),
  fetchAllOrg: fetchEntities("token/org/all-tokens")
};
const isExpectedResponse = (response) => response.ok || response.status === HTTPStatusCode.FORBIDDEN || response.status === HTTPStatusCode.UNAUTHORIZED;
const logTrackingError = (event, response) => {
  const error = new Error(response.statusText);
  logWarn(error, "Failed to track event", {
    name: event.name,
    ...pick(response, ["ok", "status", "statusText"])
  });
};
const trackEvent = async (event) => {
  const response = await fetch(
    new Request("/api/track/event", toRequestOptions(Method.POST, event))
  );
  if (!isExpectedResponse(response)) {
    logTrackingError(event, response);
  }
};
const trackEarlyEvent = async (event) => {
  const response = await fetch(
    new Request("/api/track-early/event", toRequestOptions(Method.POST, event))
  );
  if (!isExpectedResponse(response)) {
    logTrackingError(event, response);
  }
};
const trackApi = {
  trackEvent,
  trackEarlyEvent
};
const loadTraversal = (loadTraversalRequest, abortSignal) => {
  const request2 = new Request(
    "/api/traversal/execute",
    toRequestOptions(Method.POST, loadTraversalRequest, abortSignal)
  );
  return fetchJson(request2);
};
const infer = (inferRequest) => {
  const { clientRequestId, ...body } = inferRequest;
  const request2 = new Request(
    `/api/traversal/infer?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoidAndRespondWithId(request2, clientRequestId);
};
const suggest = (suggestRequest) => {
  const { clientRequestId, ...body } = suggestRequest;
  const request2 = new Request(
    `/api/traversal/suggest?requestId=${clientRequestId}`,
    toRequestOptions(Method.POST, body)
  );
  return fetchVoidAndRespondWithId(request2, clientRequestId);
};
const countInstances = ({
  startSet,
  startQuery,
  paths,
  filters,
  startSetFilterId,
  pathMatching
}) => {
  const request2 = new Request(
    "/api/traversal/counts",
    toRequestOptions(Method.POST, {
      startSet: startQuery ? null : startSet,
      startQuery,
      paths,
      filters,
      startSetFilterId,
      pathMatching
    })
  );
  return fetchJson(request2);
};
const copy$2 = (traversal, newName) => {
  return traversalApi.create({
    name: newName,
    ...omit(traversal, [
      "name",
      "_id",
      "created",
      "createdBy",
      "createdByEmail",
      "createdByName",
      "lastModifiedBy",
      "lastModifiedByEmail",
      "lastModifiedByName",
      "lastUpdated",
      "_version"
    ])
  });
};
const createTraversal = (traversal) => {
  return fetchJson(
    new Request(
      "/api/traversal/create",
      toRequestOptions(Method.POST, traversal)
    )
  );
};
const createInFolder = async (viewpoint, folderId) => {
  const viewpointResponse = await traversalApi.createTraversal(viewpoint);
  if (isArdoqError(viewpointResponse)) {
    return viewpointResponse;
  }
  const moveAssetsResponse = await workspaceFolderApi.moveAssets({
    targetFolderId: folderId,
    content: [
      { _id: viewpointResponse.traversal._id, type: AssetType.TRAVERSAL }
    ]
  });
  if (isArdoqError(moveAssetsResponse)) {
    return moveAssetsResponse;
  }
  return viewpointResponse;
};
const ensureRuleTypeId = (viewpoint) => ({
  ...viewpoint,
  pathCollapsingRules: (viewpoint.pathCollapsingRules ?? []).map((rule) => ({
    ...rule,
    referenceTypeId: rule.referenceTypeId || ardoqId()
  }))
});
const crudApiWithRuleIdCheck = () => {
  const { fetch: fetch2, fetchAll: fetchAll2, ...rest } = crudApi("traversal");
  return {
    ...rest,
    fetchAll: async () => {
      const result = await fetchAll2();
      if (isArdoqError(result)) {
        return result;
      }
      return result.map(ensureRuleTypeId);
    },
    fetch: async (id) => {
      const result = await fetch2(id);
      if (isArdoqError(result)) {
        return result;
      }
      return ensureRuleTypeId(result);
    }
  };
};
const traversalApi = {
  ...crudApiWithRuleIdCheck(),
  loadTraversal,
  infer,
  suggest,
  countInstances,
  copy: copy$2,
  createTraversal,
  createInFolder,
  rename: renameEntity("traversal")
};
const UNIFIED_CONNECTIONS_API = "/api/integrations/account";
async function fetchConnectionsList(provider) {
  const request2 = new Request(`${UNIFIED_CONNECTIONS_API}/${provider}`, {
    headers
  });
  return fetchJson(request2);
}
async function createConnection(provider, payload) {
  const request2 = new Request(
    `${UNIFIED_CONNECTIONS_API}/${provider}`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson(request2);
}
async function deleteConnection(provider, connection) {
  const request2 = new Request(
    `${UNIFIED_CONNECTIONS_API}/${provider}/${connection._id}`,
    toRequestOptions(Method.DELETE, {})
  );
  return fetchVoid(request2);
}
async function updateConnection(provider, id, payload) {
  const request2 = new Request(
    `${UNIFIED_CONNECTIONS_API}/${provider}/${id}`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchVoid(request2);
}
const unifiedIntegrationApi = {
  fetchConnectionsList,
  createConnection,
  deleteConnection,
  updateConnection
};
const fetchMetamodelPreview = async (useCaseId) => {
  const request2 = new Request(`/api/use-case/preview/${useCaseId}/metamodel`);
  return fetchJson(request2);
};
const fetchMetaModel = async (useCaseId) => {
  const request2 = new Request(`/api/use-case/view/${useCaseId}/metamodel`);
  return fetchJson(request2);
};
const loadUseCase = async (useCaseId) => {
  const request2 = new Request(
    `/api/use-case/preview/${useCaseId}/start`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
};
const useCaseApi = {
  fetchAvailable: fetchEntities("use-case/available"),
  fetchMetaModel,
  fetchMetamodelPreview,
  loadUseCase,
  fetchUseCasePreviewContent: fetchEntity("use-case/preview"),
  fetchUseCaseContent: fetchEntity("use-case/view"),
  ...crudApi("use-case")
};
const history = async ({
  id,
  type,
  maxFetchedVersions = 50
}) => {
  const request2 = new Request(
    `/api/${type}/${id}/versions/${maxFetchedVersions}`,
    {
      headers
    }
  );
  return fetchJson(request2);
};
const versionApi = {
  history
};
const fetchTypeRelationships = (workspaceIds) => {
  const request2 = new Request(
    `/api/viewpoint/type-relationships/aggregated/`,
    toRequestOptions(Method.POST, { workspaceIds })
  );
  return fetchJson(request2);
};
const fetchByComponentType = (componentType) => {
  const request2 = new Request(
    `/api/viewpoint/by/component-type/`,
    toRequestOptions(Method.POST, { "component-type-name": componentType })
  );
  return fetchJson(request2);
};
const fetchConnectedUser = () => {
  const request2 = new Request("/api/viewpoint/resource/user-connected");
  return fetchJson(request2);
};
const fetchComponents = (componentIds) => {
  const request2 = new Request(
    "/api/viewpoint/resource/component",
    toRequestOptions(Method.POST, { "component-ids": componentIds })
  );
  return fetchJson(request2);
};
const copy$1 = (viewpoint, newName) => {
  const freshViewpoint = omit(
    {
      ...viewpoint,
      name: newName,
      published: false
    },
    ["_id", "_version", "defaultForComponentTypeNames"]
  );
  return viewpointApi.create(freshViewpoint);
};
const createWithPermissions = (viewpoint) => {
  const request2 = new Request(
    "/api/viewpoint/create",
    toRequestOptions(Method.POST, {
      ...viewpoint,
      "workspace-ids": viewpoint.workspaceIds
    })
  );
  return fetchJson(request2);
};
const generateDescription$1 = async (body) => {
  const request2 = new Request(
    `/api/traversal/description/autocomplete`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const viewpointApi = {
  fetchConnectedUser,
  fetchComponents,
  fetchTypeRelationships,
  ...crudApi("viewpoint"),
  fetchByComponentType,
  fetchRestrictedWorkspaces: fetchEntities(
    "viewpoint/resource/workspace"
  ),
  copy: copy$1,
  createWithPermissions,
  generateDescription: generateDescription$1
};
const webhookApi = {
  fetchAll: fetchEntities("webhook"),
  register: createEntity("webhook"),
  delete: deleteEntity("webhook")
};
const exportWorkspace = async (id) => {
  const request2 = new Request(
    `/api/workspace/${id}/export`,
    toRequestOptions(Method.POST, null)
  );
  return fetchBlob(request2);
};
const deleteWorkspaces = async (workspaces) => {
  const request2 = new Request(
    `/api/workspace`,
    toRequestOptions(Method.DELETE, { workspaces })
  );
  return fetchAndRespondWith(request2, workspaces);
};
const createWorkspace = (workspace2) => {
  const request2 = new Request(
    `/api/workspace/create`,
    toRequestOptions(Method.POST, workspace2)
  );
  return fetchJson(request2);
};
const loadWorkspaceDigest = (workspaceId, daysAgo) => {
  const request2 = new Request(
    `/api/workspace/${workspaceId}/digest?sinceDaysAgo=${daysAgo}`,
    {
      headers
    }
  );
  return fetchJson(request2);
};
const bulkLoadWorkspaceAggregated = (workspaceIds) => {
  const request2 = new Request(
    `/api/workspace/subgraph`,
    toRequestOptions(Method.POST, { workspaceIds })
  );
  return fetchJson(request2);
};
const copy = async (workspaceId, newName, copyComponents2, copyReferences, copyCrossWorkspaceReferences) => {
  const data = {
    workspaces: [
      {
        _id: workspaceId,
        name: newName,
        copyComponents: copyComponents2,
        copyReferences,
        copyCrossWorkspaceReferences
      }
    ]
  };
  const request2 = new Request(
    `/api/workspace/copy/v2`,
    toRequestOptions(Method.POST, data)
  );
  return fetchJson(request2);
};
const copyToOrg = async (targetOrgName, workspaces) => {
  const request2 = new Request(
    `/api/workspace/copy`,
    toRequestOptions(Method.POST, { "target-org": targetOrgName, workspaces })
  );
  return fetchJson(request2);
};
const usage = async (ids) => {
  const request2 = new Request(`/api/workspace/usage?wids=${ids.join(",")}`, {
    headers
  });
  return fetchJson(request2);
};
const overview = async () => {
  const request2 = new Request("/api/workspace/overview", { headers });
  return fetchJson(request2);
};
const loadWorkspaceHierarchies = async (workspaceIds) => {
  const request2 = new Request(
    `/api/workspace/inventory?wids=${workspaceIds.join(",")}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson(request2);
};
const setGremlinEnabled = async ({
  workspaceIds,
  gremlinEnabled
}) => {
  const request2 = new Request(
    `/api/workspace/gremlin-enabled`,
    toRequestOptions(Method.POST, {
      workspaceIds,
      gremlinEnabled
    })
  );
  return fetchAndRespondWith(request2, {
    workspaceIds,
    gremlinEnabled
  });
};
const workspaceApi = {
  export: exportWorkspace,
  fetch: fetchEntity("workspace"),
  fetchAll: fetchEntities("workspace"),
  deleteWorkspaces,
  create: createWorkspace,
  bulkLoadWorkspaceAggregated,
  loadWorkspaceDigest,
  copy,
  copyToOrg,
  usage,
  overview,
  loadWorkspaceHierarchies,
  setGremlinEnabled
};
const fetchProcessComponents = async (typeIds) => {
  const request2 = new Request(
    `/api/process/component?typeIds=${typeIds.join(",")}&scopeData=true`,
    {
      headers
    }
  );
  return fetchJson(request2);
};
const fetchComponentType = async (workspaceId) => {
  const request2 = new Request(
    `/api/process/componentType?workspaceId=${workspaceId}`,
    {
      headers
    }
  );
  return fetchJson(request2);
};
const draftItems = async ({
  draftId,
  actions
}) => {
  const request2 = new Request(
    `/api/process/draft/${draftId}/items`,
    toRequestOptions(Method.POST, actions)
  );
  return fetchJson(request2);
};
const publishProcess = async ({
  process: process2,
  component: component2,
  references
}) => {
  const request2 = new Request(
    `/api/process/publish`,
    toRequestOptions(Method.POST, { process: process2, component: component2, references })
  );
  return fetchJson(request2);
};
const unpublishProcess = async ({
  processId
}) => {
  const request2 = new Request(
    `/api/process/process/${processId}/unpublish`,
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
};
const submitDraft = async ({
  draftId,
  definition,
  references
}) => {
  const request2 = new Request(
    `/api/process/draft/${draftId}/submit`,
    toRequestOptions(Method.POST, { definition, references })
  );
  return fetchJson(request2);
};
const updateProcess = async ({
  processId,
  props
}) => {
  const request2 = new Request(
    `/api/process/process/${processId}`,
    toRequestOptions(Method.PATCH, props)
  );
  return fetchJson(request2);
};
const fetchProcessSettings = async () => {
  const request2 = new Request(`/api/bpm/settings`, {
    headers
  });
  return fetchJson(request2);
};
const fetchProcessVersions = async ({
  processId
}) => {
  const request2 = new Request(`/api/bpm/process/${processId}/versions`, {
    headers
  });
  return fetchJson(request2);
};
const updateProcessSettings = async ({
  sync,
  peopleComponents,
  entityGroupSettings
}) => {
  const request2 = new Request(
    `/api/bpm/settings`,
    toRequestOptions(Method.POST, {
      sync,
      peopleComponents,
      entityGroupSettings
    })
  );
  return fetchJson(request2);
};
const initSyncProcess = async ({
  workspaceFolderName,
  workspaceName
}) => {
  const request2 = new Request(
    `/api/bpm/sync/init`,
    toRequestOptions(Method.POST, { workspaceFolderName, workspaceName })
  );
  return fetchJson(request2);
};
const processApi = {
  fetchProcessComponents,
  publishProcess,
  fetchComponentType,
  process: crudApi("process/process"),
  fetchProcessVersions,
  updateProcess,
  draft: crudApi("process/draft"),
  draftItem: (draftId) => {
    return crudApi(`process/draft/${draftId}/item`);
  },
  draftItems,
  submitDraft,
  unpublishProcess,
  fetchProcessSettings,
  updateProcessSettings,
  initSyncProcess
};
const externalDocumentApi = crudApi("external-document");
const fetchInventoryData = ({
  from,
  size,
  sorting,
  subquery,
  workspaceIds,
  subdivisions
}) => {
  const payload = {
    datasource: DataSourceType.ADVANCED_SEARCH,
    from,
    sort: sorting == null ? void 0 : sorting.sortBy,
    order: sorting == null ? void 0 : sorting.order,
    query: {
      condition: BooleanOperator.AND,
      rules: [
        {
          field: "type",
          id: "type",
          input: "select",
          operator: Operator.EQUAL,
          type: "string",
          value: SearchContext.COMPONENT
        },
        subquery
      ]
    },
    size,
    subdivisions,
    workspaceIds
  };
  const url = new URL("/api/inventory/search", window.location.origin);
  const request2 = new Request(url, toRequestOptions(Method.POST, payload));
  return fetchJson(request2);
};
const fetchComponentTypeOverview = async () => {
  const request2 = new Request(`/api/component-type/overview`, {
    headers
  });
  return fetchJson(request2);
};
const inventoryApi = {
  fetchInventoryData,
  fetchComponentTypeOverview
};
const toResponse = async (response) => {
  if (!response.ok) {
    return toResponseError(response);
  }
  try {
    const contentType = extractMimeType(
      response.headers.get("content-type") ?? ""
    );
    if (isSupportedSvgMimeType(contentType)) {
      const svg = await response.text();
      return { content: svg, contentType: ImageContentType.SVG };
    }
    if (isSupportedImageMimeType(contentType)) {
      const blob = await response.blob();
      return {
        content: URL.createObjectURL(blob),
        contentType: ImageContentType.IMAGE
      };
    }
    return toArdoqError({
      error: new Error(`File type ${contentType} is not supported`),
      userMessage: "Invalid file type"
    });
  } catch (err) {
    return toArdoqError({
      error: new Error("Failed to parse content"),
      userMessage: "Unable to parse content"
    });
  }
};
const fetchImagePreview = (url) => {
  const request2 = new Request(url);
  return window.fetch(request2).then((response) => toResponse(response)).catch((error) => handleNetworkError(request2, error));
};
const imagePreviewApi = {
  fetchImagePreview
};
const importFromUseCases = () => {
  const request2 = new Request(
    "/api/org-triple/import-use-cases",
    toRequestOptions(Method.POST, {})
  );
  return fetchJson(request2);
};
const orgTripleApi = {
  ...crudApi("org-triple"),
  importFromUseCases
};
const integrationNotificationApi = {
  fetchUsers: async () => {
    return fetchJson(
      new Request("/api/integrations/notification-settings/users/details")
    );
  },
  postUser: async (body) => {
    return fetchJson(
      new Request(
        "/api/integrations/notification-settings/users/modify",
        toRequestOptions(Method.POST, body)
      )
    );
  }
};
const exhaustiveCheck = (value) => {
  throw new Error(`Unhandled value: ${value}`);
};
function getUrl(integrationType, requestId) {
  if (isCloudProvider(integrationType)) {
    return `/api/integrations/tabular/cloud/${integrationType}?async=true&requestId=${requestId}`;
  }
  if (isUnifiedIntegrationProvider(integrationType)) {
    return `/api/integrations/unified/${integrationType}/fetch?async=true&requestId=${requestId}`;
  }
  if (integrationType === "microsoftgraph") {
    return `/api/integrations/tabular/${integrationType}?async=true&requestId=${requestId}`;
  }
  if (integrationType === "servicenow") {
    return `/api/integrations/tabular/${integrationType}?async=true&requestId=${requestId}`;
  }
  return exhaustiveCheck(integrationType);
}
function fetchSelectionAsync({
  integrationType,
  fetchRequestPayload,
  requestId
}) {
  const url = getUrl(integrationType, requestId);
  const request2 = new Request(
    url,
    toRequestOptions(Method.POST, fetchRequestPayload)
  );
  return fetchIntegrationJson(request2);
}
const selectionApi = {
  fetchSelectionAsync
};
const pathFragment = "bookmark";
const bookmarkApi = {
  rename: renameEntity(pathFragment),
  ...crudApi(pathFragment),
  createInFolder: (attributes, folderId) => assetApi.createInFolder(attributes, folderId, AssetType.BOOKMARK)
};
const fetchStatus = (timeout) => {
  const request2 = new Request("/api/status", {
    signal: timeout ? AbortSignal.timeout(timeout) : void 0
  });
  return fetchJson(request2);
};
const statusApi = {
  fetchStatus
};
const submitBusinessCapabilityMap = async (body) => {
  const request2 = new Request(
    `/api/use-case/augmentations/business-capability-map/generate`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const submitLinking = async (body) => {
  const request2 = new Request(
    `/api/use-case/augmentations/business-capability-map/link-value-stream`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const submitValueStream = async (body) => {
  const request2 = new Request(
    `/api/use-case/augmentations/value-stream/generate`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const submitValueProposition = async (body) => {
  const request2 = new Request(
    `/api/use-case/augmentations/value-proposition/submit`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const generateDescription = async (body) => {
  const request2 = new Request(
    `/api/use-case/augmentations/value-proposition/autocomplete`,
    toRequestOptions(Method.POST, body)
  );
  return fetchJson(request2);
};
const prefillAugmentations = async () => {
  const request2 = new Request(
    `/api/use-case/augmentations/value-stream/prefill`
  );
  return fetchJson(request2);
};
const augmentationsApi = {
  generateDescription,
  submitValueStream,
  prefillAugmentations,
  submitValueProposition,
  submitBusinessCapabilityMap,
  submitLinking
};
const fetchTasks = async () => {
  const request2 = new Request("/api/tasks", { headers });
  return fetchJson(request2);
};
const taskApi = {
  fetchTasks
};
export {
  FavoriteAction,
  Method,
  SSOMappingApi,
  UNAUTHORIZED_ERROR,
  advancedSearchApi,
  aiSuggestionApi,
  allowlistApi,
  api,
  assetApi,
  attachmentApi,
  auditLogApi,
  augmentationsApi,
  authApi,
  awsIntegrationApi,
  azureIntegrationApi,
  batchApi,
  bestPracticeAssistantApi,
  bookmarkApi,
  broadcastApi,
  bundleApi,
  cloudProviderApi,
  commonIntegrationApi,
  componentApi,
  componentTypeApi,
  dashboardApi,
  dashboardColorPaletteApi,
  datasourceApi,
  discoverConfigurationApi,
  discoverLinkApi,
  dynamicFilterApi,
  entityGroupApi,
  externalDocumentApi,
  featuresApi,
  fieldApi,
  folderApi,
  getErrorStatusCode,
  graphSearchApi,
  groupApi,
  handleError,
  handleNetworkError,
  historyApi,
  imagePreviewApi,
  inferenceApi,
  integrationNotificationApi,
  integrationsJobApi,
  integrationsLucidChartApi,
  integrationsOAuthApi,
  inventoryApi,
  invitationApi,
  jobApi,
  logApi,
  logWithMessage,
  loginServiceApi,
  maybeEmitUnauthorizedEvent,
  membershipApi,
  metamodel2Api,
  metamodelApi,
  metamodelSyncApi,
  microsoftGraphApi,
  microsoftGraphIntegrationApi,
  migrationChecklistApi,
  modelApi,
  notificationApi,
  orgTripleApi,
  organizationApi,
  permissionApi,
  permissionZoneApi,
  presentationApi,
  privilegeApi,
  processApi,
  referenceApi,
  referenceTypeApi,
  reportApi,
  requestAccessApi,
  restoreApi,
  roleApi,
  scenarioApi,
  scimApi,
  searchApi,
  selectionApi,
  serviceAccountApi,
  servicenowConfigApi,
  servicenowIntegrationApi,
  signavioExporterApi,
  slideApi,
  statusApi,
  storedQueryApi,
  subdivisionApi,
  suggestionApi,
  surveyApi,
  tabularApi,
  tabularMappingApi,
  tagApi,
  taskApi,
  templateApi,
  toApiResponse,
  toRequestOptions,
  tokenApi,
  trackApi,
  traversalApi,
  unifiedApi,
  unifiedIntegrationApi,
  useCaseApi,
  userApi,
  versionApi,
  viewpointApi,
  webhookApi,
  workspaceApi,
  workspaceFolderApi
};
//# sourceMappingURL=index.js.map
